<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("modern_trade.sale_order.detail_title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right">
          <base-button size="sm" type="default" @click="goBack">
            <i class="fa fa-arrow-left"></i> {{ $t("back") || "Back" }}
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6" v-loading="loading">
      <template v-if="so">
        <!-- #303: ปิดใบ = สถานะสุดท้าย — แก้ไขอะไรไม่ได้อีก (BE ตีกลับ 409 sale_order_closed) -->
        <div v-if="isClosed" class="alert alert-success mb-4">
          <i class="fa fa-lock"></i>
          <strong>{{ $t("modern_trade.sale_order.closed_banner") }}</strong>
          <span class="ml-1">{{ $t("modern_trade.sale_order.closed_banner_hint") }}</span>
        </div>

        <!-- Header Card -->
        <card class="mb-4" ref="headerCard" :class="{ 'mt-focus-highlight': focusHighlight === 'headerCard' }">
          <div class="row">
            <div class="col-md-2">
              <small class="text-muted d-block">{{ $t("modern_trade.sale_order.so_no") }}</small>
              <strong>{{ so.so_no }}</strong>
            </div>
            <div class="col-md-3">
              <small class="text-muted d-block">{{ $t("modern_trade.sale_order.company") }}</small>
              <strong>{{ so.company_name || so.company_id }}</strong>
            </div>
            <div class="col-md-2">
              <small class="text-muted d-block">{{ $t("modern_trade.sale_order.sale_type") }}</small>
              <span class="mt-pill" :style="mtSaleTypeStyle(so.sale_type)">{{ mtSaleTypeLabel(so.sale_type) }}</span>
            </div>
            <div class="col-md-3">
              <small class="text-muted d-block">{{ $t("modern_trade.sale_order.shipping_type") }}</small>
              <span>{{ shippingTypeLabel(so.shipping_type) }}</span>
            </div>
            <div class="col-md-2">
              <small class="text-muted d-block">{{ $t("modern_trade.sale_order.status") }}</small>
              <span class="mt-pill" :style="mtStatusStyle(so.status)">{{ mtStatusLabel(so.status) }}</span>
              <span v-if="needsLot && goodsProgressState.state === 'partial'" class="badge badge-warning ml-1">
                {{ $t("modern_trade.lot_form.badge_partial", { received: goodsProgressState.received, ordered: goodsProgressState.ordered }) }}
              </span>
              <span v-else-if="needsLot && goodsProgressState.state === 'full'" class="badge badge-success ml-1">
                {{ $t("modern_trade.lot_form.badge_full") }}
              </span>
            </div>
          </div>
          <hr class="my-3" />
          <div class="row">
            <div class="col-md-3">
              <small class="text-muted d-block">{{ $t("modern_trade.sale_order.po_number") }}</small>
              <strong>{{ so.po_number }}</strong>
              <i
                class="fa fa-file-pdf text-danger ml-2"
                style="cursor:pointer;font-size:1.1rem"
                :title="$t('modern_trade.sale_order.download_po')"
                @click="openPoPdf"
              ></i>
              <!-- #183: openPoPdf now routes to the print-preview page, so keep the
                   uploaded PO attachment reachable via its own paperclip icon. -->
              <i
                class="fa fa-paperclip text-muted ml-2"
                style="cursor:pointer;font-size:1.05rem"
                :title="$t('modern_trade.sale_order.po_file')"
                @click="downloadPoFile"
              ></i>
            </div>
            <!-- Legacy SOs only — MT stopped creating BO orders, so new sale orders
                 simply have no bo_order and both columns drop out entirely. -->
            <div class="col-md-3" v-if="hasBoOrder">
              <small class="text-muted d-block">{{ $t("modern_trade.sale_order.bo_order") }}</small>
              <strong>#{{ boOrder.id || boOrder.order_id }}</strong>
              <span v-if="boOrder.status_text" class="badge badge-secondary ml-1">{{ boOrder.status_text }}</span>
            </div>
            <div class="col-md-3" v-if="hasBoOrder">
              <small class="text-muted d-block">{{ $t("modern_trade.sale_order.tracking") }}</small>
              <strong>{{ boOrder.tracking_number || boOrder.shipping_tracking_number || "-" }}</strong>
            </div>
            <div class="col-md-3 ml-auto text-right">
              <small class="text-muted d-block">{{ $t("modern_trade.sale_order.net_amount") }}</small>
              <h3 class="text-primary mb-0">{{ money(so.net_amount) }}</h3>
            </div>
          </div>
          <div class="row mt-2" v-if="so.note">
            <div class="col-12"><small class="text-muted">{{ $t("modern_trade.sale_order.note") }}: {{ so.note }}</small></div>
          </div>
        </card>

        <el-tabs v-model="activeTab" type="card" class="mt-so-tabs">
        <el-tab-pane :label="$t('modern_trade.sale_order.tab_items')" name="items">
        <!-- Items Card -->
        <card class="mb-4">
          <template slot="header">
            <div class="d-flex justify-content-between align-items-center">
              <h3 class="mb-0">{{ $t("modern_trade.sale_order.items") }}</h3>
              <!-- #303: canMutate = แก้ไขได้ + ยังไม่ปิดใบ/ยกเลิก -->
              <div v-if="canMutate">
                <!-- spec 2026-11-03 §2: drafts are edited in one place only — the full-page editor -->
                <base-button
                  v-if="canEdit"
                  size="sm"
                  type="primary"
                  @click="goEdit"
                >
                  <i class="fa fa-edit"></i> {{ $t("edit") }}
                </base-button>
                <base-button
                  v-if="so.status === 1"
                  size="sm"
                  type="success"
                  :loading="confirming"
                  @click="handleConfirm"
                >
                  <i class="fa fa-lock"></i> {{ $t("modern_trade.sale_order.confirm") }}
                </base-button>
                <base-button
                  v-if="so.status === 1 || so.status === 2"
                  size="sm"
                  type="danger"
                  :loading="cancelling"
                  @click="handleCancel"
                >
                  <i class="fa fa-times"></i> {{ $t("modern_trade.sale_order.cancel") }}
                </base-button>
                <!-- #154: ปิดใบ — delivered (5) → closed (6) -->
                <base-button
                  v-if="so.status === 5"
                  size="sm"
                  type="success"
                  :loading="closing"
                  @click="handleClose"
                >
                  <i class="fa fa-check-circle"></i> {{ $t("modern_trade.sale_order.close") }}
                </base-button>
              </div>
            </div>
          </template>

          <!-- full-bleed: ยืดตารางชนขอบการ์ดซ้าย-ขวา และชิดหัวการ์ด (card-body padding 1.5rem) -->
          <div style="margin: -1.5rem -1.5rem 1rem">
          <el-table :data="items" header-row-class-name="thead-light" size="small" style="width: 100%">
            <el-table-column :label="$t('modern_trade.sale_order.product')" min-width="400">
              <template slot-scope="{ row }">
                <div class="d-flex align-items-center justify-content-between">
                  <span><span v-if="row.product_code">{{ row.product_code }} - </span>{{ row.product_name }}</span>
                  <base-button
                    size="sm"
                    type="secondary"
                    class="py-1 px-2 ml-2"
                    :title="$t('modern_trade.sale_order.product_detail')"
                    @click.native="openProductPopup(row)"
                  >
                    <i class="fa fa-info-circle"></i>
                  </base-button>
                </div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.qty')" min-width="80" align="center">
              <template slot-scope="{ row }">{{ row.qty }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.retail_price')" min-width="100" align="right">
              <template slot-scope="{ row }">{{ money(row.retail_price) }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.gp_percent') || 'GP %'" min-width="80" align="right">
              <template slot-scope="{ row }">{{ row.gp_percent != null ? Number(row.gp_percent).toFixed(2) : "-" }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.unit_price')" min-width="100" align="right">
              <template slot-scope="{ row }">{{ money(row.unit_price) }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.discount')" min-width="100" align="right">
              <template slot-scope="{ row }">{{ money(row.discount) }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.amount')" min-width="110" align="right">
              <template slot-scope="{ row }">{{ money(row.amount) }}</template>
            </el-table-column>
          </el-table>
          </div>

          <div class="row mt-3 justify-content-end">
            <div class="col-md-4">
              <div class="d-flex justify-content-between">
                <span class="text-muted">{{ $t("modern_trade.sale_order.sub_total") }}</span>
                <strong>{{ money(so.sub_total) }}</strong>
              </div>
              <div class="d-flex justify-content-between align-items-center mt-1">
                <span class="text-muted">{{ $t("modern_trade.sale_order.bill_discount") }}</span>
                <strong>{{ money(so.bill_discount) }}</strong>
              </div>
              <div class="d-flex justify-content-between mt-1">
                <span class="text-muted">{{ $t("modern_trade.sale_order.vat_amount") }}</span>
                <strong>{{ money(so.vat_amount) }}</strong>
              </div>
              <hr class="my-2" />
              <div class="d-flex justify-content-between">
                <span><strong>{{ $t("modern_trade.sale_order.net_amount") }}</strong></span>
                <strong class="text-primary">{{ money(so.net_amount) }}</strong>
              </div>
            </div>
          </div>
        </card>
        </el-tab-pane>

        <el-tab-pane :label="$t('modern_trade.sale_order.tab_warehouse')" name="warehouse" lazy>
        <!-- Production Orders Card -->
        <card v-if="showProductionCard" class="mb-4" ref="productionCard" :class="{ 'mt-focus-highlight': focusHighlight === 'productionCard' }">
          <template slot="header">
            <div class="d-flex justify-content-between align-items-center">
              <h3 class="mb-0">{{ $t("modern_trade.sale_order.production_orders") }}</h3>
              <base-button
                v-if="canMutate && so.status >= 2 && productionOrders.length === 0"
                size="sm"
                type="primary"
                :loading="genningMo"
                @click="handleGenProductionOrders"
              >
                <i class="fa fa-industry"></i> {{ $t("modern_trade.sale_order.gen_production_orders") }}
              </base-button>
            </div>
          </template>
          <div style="margin: -1.5rem -1.5rem -1.5rem">
          <el-table :data="productionOrders" header-row-class-name="thead-light" size="small" empty-text="-" style="width: 100%">
            <el-table-column label="MO No." prop="doc_no" min-width="130"></el-table-column>
            <el-table-column :label="$t('modern_trade.product.manufacturer') || 'ผู้ผลิต'" prop="manufacturer" min-width="180"></el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.status')" min-width="110" align="center">
              <template slot-scope="{ row }">
                <span class="badge badge-secondary">{{ moStatusLabel(row.status) }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('actions')" min-width="200" align="center">
              <template slot-scope="{ row }">
                <base-button size="sm" type="info" @click.native="downloadMoPdf(row)">
                  <i class="fa fa-file-pdf"></i> {{ $t("modern_trade.sale_order.production_order_pdf") }}
                </base-button>
              </template>
            </el-table-column>
          </el-table>
          </div>
        </card>

        <!-- Goods Import (Lot/Expire) Card -->
        <card v-if="showGoodsCard" class="mb-4">
          <template slot="header">
            <div class="d-flex justify-content-between align-items-center flex-wrap">
              <h3 class="mb-0">
                {{ $t("modern_trade.sale_order.goods_import") }}
                <span v-if="goodsProgressState.state === 'partial'" class="badge badge-warning ml-2">
                  {{ $t("modern_trade.lot_form.badge_partial", { received: goodsProgressState.received, ordered: goodsProgressState.ordered }) }}
                </span>
                <span v-else-if="goodsProgressState.state === 'full'" class="badge badge-success ml-2">
                  {{ $t("modern_trade.lot_form.badge_full") }}
                </span>
              </h3>
              <base-button v-if="canReceiveGoods" size="sm" type="success" @click.native="downloadSoLotTemplate">
                <i class="fa fa-file-excel"></i> {{ $t("modern_trade.sale_order.lot_template") }}
              </base-button>
            </div>
          </template>

          <!-- Nov-5 §4: manual Lot / expiry entry (partial receipts) -->
          <div v-if="canReceiveGoods && lotProducts.length" class="mb-4 mt-lot-form">
            <h4>{{ $t("modern_trade.lot_form.title") }}</h4>
            <p class="small text-muted mb-2">{{ $t("modern_trade.lot_form.hint", { date: lotMinExpiry }) }}</p>
            <div v-for="p in lotProducts" :key="'lp_' + p.product_id" class="mt-lot-product">
              <div class="d-flex justify-content-between align-items-center flex-wrap">
                <div>
                  <strong>{{ lotProductLabel(p).text }}</strong>
                  <span v-if="lotProductLabel(p).noBarcode" class="badge badge-secondary ml-1">{{ $t("modern_trade.common.no_barcode") }}</span>
                  <span class="small text-muted ml-2">
                    {{ $t("modern_trade.lot_form.to_receive") }}: <strong>{{ p.remaining }}</strong>
                    · {{ $t("modern_trade.lot_form.entered") }}: <strong>{{ lotEnteredQty(p.product_id) }}</strong>
                  </span>
                </div>
                <base-button size="sm" type="primary" outline :disabled="Number(p.remaining) <= 0" @click.native="addLotRow(p.product_id)">
                  <i class="fa fa-plus"></i> {{ $t("modern_trade.lot_form.add_lot") }}
                </base-button>
              </div>
              <div v-if="lotCheck.productErrors[p.product_id]" class="text-danger small mt-1">
                {{ lotErrorText(lotCheck.productErrors[p.product_id]) }}
              </div>
              <div v-for="e in lotRowsOf(p.product_id)" :key="'lr_' + e.row.uid" class="row align-items-start mt-2">
                <div class="col-md-4 mb-1">
                  <input v-model="e.row.lot_no" type="text" class="form-control form-control-sm" :placeholder="$t('modern_trade.lot_form.lot_no')" />
                </div>
                <div class="col-md-3 mb-1">
                  <el-date-picker
                    v-model="e.row.expired_date"
                    type="date"
                    size="small"
                    value-format="yyyy-MM-dd"
                    format="dd/MM/yyyy"
                    style="width: 100%"
                    :picker-options="lotPickerOptions"
                    :placeholder="$t('modern_trade.sale_order.expired_date')"
                  ></el-date-picker>
                </div>
                <div class="col-md-3 mb-1">
                  <input v-model="e.row.qty" type="number" min="1" step="1" class="form-control form-control-sm" :placeholder="$t('modern_trade.sale_order.qty')" />
                </div>
                <div class="col-md-2 mb-1">
                  <base-button size="sm" type="danger" outline @click.native="removeLotRow(e.idx)">
                    <i class="fa fa-trash"></i>
                  </base-button>
                </div>
                <div v-if="lotRowError(e.idx)" class="col-12 text-danger small">{{ lotErrorText(lotRowError(e.idx)) }}</div>
              </div>
            </div>
            <div class="mt-3">
              <base-button type="success" :loading="savingLots" :disabled="!lotCheck.ok || savingLots" @click="saveLots">
                <i class="fa fa-save"></i> {{ $t("modern_trade.lot_form.save") }}
              </base-button>
              <span v-if="lotCheck.formError" class="text-muted small ml-2">{{ lotErrorText(lotCheck.formError) }}</span>
            </div>
          </div>

          <div v-if="canReceiveGoods">
            <!-- Step 1: pick file -->
            <div v-if="wizardStep === 1" class="row align-items-end">
              <div class="col-md-6">
                <label class="form-control-label">{{ $t("modern_trade.import.pick_file") }} (.xls / .xlsx)</label>
                <input ref="goodsFileInput" type="file" accept=".xls,.xlsx" class="form-control" @change="onGoodsFileChange" />
              </div>
              <div class="col-md-6 pb-1">
                <base-button type="primary" :loading="validating" :disabled="!goodsFile" @click="doValidate">
                  <i class="fa fa-check"></i> {{ $t("modern_trade.import.validate") }}
                </base-button>
              </div>
            </div>

            <!-- Step 2: preview -->
            <div v-if="wizardStep === 2">
              <div class="mb-3">
                <span class="badge badge-secondary mr-2">{{ $t("modern_trade.import.summary_total") }}: {{ batch.row_total }}</span>
                <span class="badge badge-success mr-2">{{ $t("modern_trade.import.summary_ok") }}: {{ batch.row_ok }}</span>
                <span class="badge badge-danger mr-2">{{ $t("modern_trade.import.summary_error") }}: {{ batch.row_error }}</span>
              </div>
              <el-table :data="previewRows" header-row-class-name="thead-light" size="small">
                <el-table-column :label="$t('modern_trade.import.row_no')" prop="row_no" width="60" align="center"></el-table-column>
                <el-table-column :label="$t('modern_trade.sale_order.product')" min-width="150">
                  <template slot-scope="{ row }">{{ row.product_name || row.product_code }}</template>
                </el-table-column>
                <el-table-column label="Lot No." prop="lot_no" min-width="100"></el-table-column>
                <el-table-column :label="$t('modern_trade.sale_order.expired_date')" prop="expired_date" min-width="110"></el-table-column>
                <el-table-column :label="$t('modern_trade.sale_order.qty')" prop="qty" min-width="70" align="right"></el-table-column>
                <el-table-column :label="$t('modern_trade.import.status_ok')" min-width="90" align="center">
                  <template slot-scope="{ row }">
                    <span class="badge" :class="row.status === 1 ? 'badge-success' : 'badge-danger'">
                      {{ row.status === 1 ? $t("modern_trade.import.status_ok") : $t("modern_trade.import.status_error") }}
                    </span>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('modern_trade.import.error_message')" min-width="200">
                  <template slot-scope="{ row }">
                    <span v-if="row.error_msg" class="text-danger">{{ lotErrorText(row.error_msg) }}</span>
                  </template>
                </el-table-column>
              </el-table>
              <div class="mt-3">
                <base-button type="secondary" @click="resetWizard">
                  <i class="fa fa-arrow-left"></i> {{ $t("modern_trade.import.cancel") }}
                </base-button>
                <base-button type="success" :loading="confirmingGoods" :disabled="batch.row_ok === 0 || batch.row_error > 0" @click="doConfirmGoods">
                  <i class="fa fa-check"></i> {{ $t("modern_trade.import.confirm") }}
                </base-button>
              </div>
            </div>
          </div>

          <!-- Committed lots -->
          <div v-if="receivedLots.length" class="mt-3">
            <h4>{{ $t("modern_trade.sale_order.received_lots") || "ล็อตที่รับเข้า" }}</h4>
            <el-table :data="receivedLots" header-row-class-name="thead-light" size="small">
              <el-table-column :label="$t('modern_trade.sale_order.product')" min-width="180">
                <template slot-scope="{ row }"><span v-if="row.product_code">{{ row.product_code }} - </span>{{ row.product_name }}</template>
              </el-table-column>
              <el-table-column label="Lot No." prop="lot_no" min-width="110"></el-table-column>
              <el-table-column :label="$t('modern_trade.sale_order.expired_date')" prop="expired_date" min-width="110"></el-table-column>
              <el-table-column :label="$t('modern_trade.sale_order.qty_received') || 'รับเข้า'" prop="qty_received" min-width="80" align="right"></el-table-column>
              <el-table-column :label="$t('modern_trade.sale_order.qty_delivered') || 'ส่งแล้ว'" prop="qty_delivered" min-width="80" align="right"></el-table-column>
              <el-table-column :label="$t('modern_trade.sale_order.qty_available') || 'คงเหลือ'" prop="qty_available" min-width="80" align="right"></el-table-column>
            </el-table>
          </div>
        </card>

        <!-- Deliveries Card -->
        <card v-if="showDeliveryCard" class="mb-4" ref="deliveryCard" :class="{ 'mt-focus-highlight': focusHighlight === 'deliveryCard' }">
          <template slot="header">
            <div class="d-flex justify-content-between align-items-center">
              <h3 class="mb-0">{{ $t("modern_trade.sale_order.deliveries") }}</h3>
              <base-button
                v-if="showCreateDelivery"
                size="sm"
                type="primary"
                @click="openDeliveryDialog"
              >
                <i class="fa fa-plus"></i> {{ $t("modern_trade.sale_order.create_delivery") }}
              </base-button>
            </div>
          </template>
          <div style="margin: -1.5rem -1.5rem -1.5rem">
          <el-table :data="deliveries" header-row-class-name="thead-light" size="small" empty-text="-" style="width: 100%">
            <el-table-column label="DO No." min-width="120">
              <template slot-scope="{ row }">
                <span :class="{ 'text-muted': row.status === 9 }">{{ row.doc_no }}</span>
                <div v-if="row.status === 9">
                  <span class="badge badge-secondary">{{ $t("modern_trade.delivery.cancel.cancelled_badge") }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.delivery_date')" min-width="110">
              <template slot-scope="{ row }">{{ formatDate(row.delivery_date) }}</template>
            </el-table-column>
            <!-- round-19 Dev-B — ship status badge + who/when -->
            <el-table-column :label="$t('modern_trade.delivery.ship_status.label')" min-width="150">
              <template slot-scope="{ row }">
                <span class="mt-pill" :style="mtShipStatusStyle(row.ship_status || 1)">
                  {{ mtShipStatusLabel(row.ship_status || 1) }}
                </span>
                <div v-if="row.ship_status === 2 && row.shipped_by_name" class="text-muted small mt-1">
                  {{ row.shipped_by_name }} · {{ formatDate(row.shipped_at) }}
                </div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.delivery.ship_status.tracking_number')" min-width="130">
              <template slot-scope="{ row }">{{ doTracking(row) || "-" }}</template>
            </el-table-column>
            <!-- #504 — ปุ่มสร้างออเดอร์เข้าห้องแพ็ค (หนึ่งใบส่งของ = หนึ่งออเดอร์ที่ยังมีชีวิต)
                 ทุกอย่างในคอลัมน์นี้มาจาก GET .../bo-order/preview ของ BE ล้วน ๆ:
                 ปุ่มกดได้/ไม่ได้, เหตุผล, และเลขออเดอร์ที่สร้างไปแล้ว หน้าจอไม่ตัดสินเอง
                 เพราะเงื่อนไข (LOT/วันหมดอายุ, การผูก WMS, ตั้งค่าโมดูล) อยู่ฝั่ง BE ทั้งหมด -->
            <el-table-column :label="$t('modern_trade.delivery.bo_order.column')" min-width="210">
              <template slot-scope="{ row }">
                <div v-if="doLiveOrder(row)">
                  <a
                    :href="`/backoffice/orders/${doLiveOrder(row).id}`"
                    target="_blank"
                    rel="noopener"
                    class="btn btn-sm btn-primary"
                  >
                    <i class="fa fa-external-link-alt"></i>
                    {{ $t("modern_trade.delivery.bo_order.view_action", { id: doLiveOrder(row).id }) }}
                  </a>
                  <div class="text-muted small mt-1">{{ boOrderStatusText(doLiveOrder(row)) }}</div>
                  <!-- เลขพัสดุว่าง = ขนส่งบางเจ้าออกเลขแบบ async เลขจะตามมาทีหลัง
                       ต้องบอกว่า "กำลังขอ" ไม่ใช่ปล่อยช่องว่างให้เดาว่าพัง -->
                  <div class="small" :class="doLiveOrder(row).tracking_number ? 'text-muted' : 'text-warning'">
                    {{ doLiveOrder(row).tracking_number || $t("modern_trade.delivery.bo_order.tracking_pending") }}
                  </div>
                </div>
                <div v-else-if="doShowCreateButton(row)">
                  <base-button
                    v-if="canMutate"
                    size="sm"
                    type="primary"
                    :disabled="!doCanCreate(row)"
                    @click.native="openBoOrderDialog(row)"
                  >
                    <i class="fa fa-dolly"></i>
                    {{ $t("modern_trade.delivery.bo_order.create_action") }}
                  </base-button>
                  <div v-if="!doCanCreate(row)" class="text-muted small mt-1">{{ doReasonText(row) }}</div>
                </div>
                <span v-else class="text-muted">-</span>
              </template>
            </el-table-column>
            <!-- spec 2026-11-03 §3A: ขายขาดออกใบกำกับ 1 ใบต่อ 1 ใบส่งของ ก่อนรับเงิน
                 ใบส่งของที่ออกใบแล้วโชว์เลขใบแทนปุ่ม (ยกเลิกใบ = invoice_id กลับเป็น NULL
                 ปุ่มจึงกลับมาเอง) — 409 ของ BE เป็นด่านสุดท้ายเสมอ -->
            <el-table-column
              v-if="isOutright"
              :label="$t('modern_trade.invoice.delivery_invoice_col')"
              min-width="170"
              align="center"
            >
              <template slot-scope="{ row }">
                <span v-if="row.invoice_id" class="badge badge-success">
                  {{ deliveryInvoiceDocNo(row) }}
                </span>
                <base-button
                  v-else-if="canIssueFromDeliveryRow(row)"
                  size="sm"
                  type="primary"
                  :loading="issuingDeliveryId === row.id"
                  :disabled="issuingDeliveryId !== null"
                  @click.native="handleIssueFromDelivery(row)"
                >
                  <i class="fa fa-file-invoice"></i> {{ $t("modern_trade.invoice.issue_from_delivery") }}
                </base-button>
                <span v-else class="text-muted">-</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('actions')" min-width="200" align="center">
              <template slot-scope="{ row }">
                <!-- Neutral label on purpose: this opens an edit dialog (status +
                     tracking number) and no longer flips the status on one click,
                     so a "mark as shipped" wording would promise the wrong thing. -->
                <base-button
                  v-if="canMutate && row.status !== 9"
                  size="sm"
                  :type="row.ship_status === 2 ? 'secondary' : 'success'"
                  @click.native="openShipStatusDialog(row)"
                >
                  <i class="fa fa-truck"></i>
                  {{ $t("modern_trade.delivery.ship_status.edit_action") }}
                </base-button>
                <base-button size="sm" type="info" @click.native="downloadDoPdf(row)">
                  <i class="fa fa-file-pdf"></i> {{ $t("modern_trade.sale_order.delivery_pdf") }}
                </base-button>
                <!-- 2026-11-04: ยกเลิกใบส่งของที่ยังไม่ถูกใช้ (BE ตรวจซ้ำทุกเงื่อนไข) -->
                <base-button
                  v-if="canCancelDeliveryRow(row)"
                  size="sm"
                  type="danger"
                  :loading="cancellingDeliveryId === row.id"
                  :disabled="cancellingDeliveryId !== null"
                  @click.native="handleCancelDelivery(row)"
                >
                  <i class="fa fa-ban"></i> {{ $t("modern_trade.delivery.cancel.action") }}
                </base-button>
              </template>
            </el-table-column>
          </el-table>
          <!-- ข้อแลกเปลี่ยนที่ยอมรับอย่างรู้ตัว (spec 2026-09-07 §3 case C): DO ที่ไม่ได้
               สร้างออเดอร์ ระบบไม่ตัดสต็อกให้เลย ถ้าไม่เขียนไว้ตรงนี้ ผู้ใช้จะเข้าใจว่า
               ระบบจัดการให้แล้ว และตัวเลขคลังจะสูงกว่าของจริงไปเรื่อย ๆ -->
          <div class="px-3 py-2 small text-muted border-top">
            <i class="fa fa-exclamation-triangle text-warning"></i>
            {{ $t("modern_trade.delivery.bo_order.manual_ship_hint") }}
          </div>
          </div>
        </card>

        <!-- ใบร่าง (status 1) และใบยกเลิก (status 9) ทำให้การ์ดทั้งสามใบข้างบนไม่แสดงเลย
             แท็บถูกทำให้ต่อเป็นก้อนเดียวกับการ์ดใบแรกแล้ว ถ้าปล่อยว่างจะเหลือหัวแท็บลอย
             ไม่มีตัวรับ — การ์ดใบนี้จึงต้องเป็น "ใบสุดท้าย" ของแท็บ เพื่อให้ตกเป็น
             :first-child เองเมื่อไม่มีใบอื่นแสดง -->
        <card v-if="!showProductionCard && !showGoodsCard && !showDeliveryCard" class="mb-4">
          <div class="text-center text-muted py-3">{{ $t("modern_trade.sale_order.warehouse_empty") }}</div>
        </card>
        </el-tab-pane>

        <el-tab-pane :label="$t('modern_trade.sale_order.tab_finance')" name="finance" lazy>
        <!-- Payment / Invoice Card — บันทึกรับเงินหลังส่งของ แล้วระบบออกใบกำกับภาษีให้อัตโนมัติ -->
        <card class="mb-4" ref="financeCard" :class="{ 'mt-focus-highlight': focusHighlight === 'financeCard' }">
          <template slot="header">
            <div class="d-flex justify-content-between align-items-center">
              <h3 class="mb-0">{{ $t("modern_trade.payment.title") }} / {{ $t("modern_trade.invoice.title") }}</h3>
              <div>
                <!-- ฝากขาย (sale_type 1): เงินเข้าตามยอดขายจริง ไม่ใช่ยอดส่งของ —
                     รับเงิน/ออกใบกำกับภาษีทำที่หน้าใบกำกับภาษีเท่านั้น (BE ตีกลับ 409
                     `wrong_sale_type`) จึงซ่อนปุ่มทั้งคู่แล้วบอกทางที่ถูกด้วย alert ด้านล่าง -->
                <base-button
                  v-if="canMutate && !isConsignment && deliveries.length > 0"
                  size="sm"
                  type="success"
                  @click="openPaymentDialog"
                >
                  <i class="fa fa-money-bill"></i> {{ $t("modern_trade.payment.record") }}
                </base-button>
                <base-button
                  v-if="canMutate && !isConsignment && showSource3Button"
                  size="sm"
                  type="primary"
                  :loading="issuingInvoice"
                  @click="handleIssueInvoice"
                >
                  <i class="fa fa-file-invoice"></i>
                  {{ $t("modern_trade.invoice.issue") }} ({{ money(paymentCap.uninvoiced_amount) }})
                </base-button>
              </div>
            </div>
          </template>
          <!-- ฝากขาย: บอกทางที่ถูกแทนปุ่มที่ซ่อนไป (ประวัติรับเงินเดิมยังแสดงตามปกติ)
               spec 2026-08-13: ปลายทางคือหน้า "รับเงินฝากขาย" (เงินผูกกับบริษัทคู่ค้า)
               ไม่ใช่หน้าใบกำกับภาษีอีกต่อไป -->
          <div v-if="isConsignment" class="alert alert-info mb-3">
            <i class="fa fa-info-circle"></i> {{ $t("modern_trade.payment.consignment_hint") }}
            <router-link :to="{ name: 'MtConsignmentPayments' }" class="alert-link">
              {{ $t("modern_trade.payment.consignment_link") }}
            </router-link>
          </div>
          <!-- #303: ปิดใบแล้วรับเงินเพิ่มไม่ได้ — บอกเหตุผลแทนปุ่มที่หายไป -->
          <div v-if="isClosed && deliveries.length > 0" class="alert alert-secondary mb-3">
            <i class="fa fa-lock"></i> {{ $t("modern_trade.payment.closed_no_more") }}
          </div>
          <div v-if="!isConsignment && deliveries.length === 0" class="alert alert-info mb-0">
            <i class="fa fa-info-circle"></i> {{ $t("modern_trade.payment.after_delivery_hint") }}
          </div>
          <template v-else>
            <div style="margin: -1.5rem -1.5rem 1rem">
            <el-table :data="invoices" header-row-class-name="thead-light" size="small" empty-text="-" style="width: 100%">
              <el-table-column :label="$t('modern_trade.invoice.doc_no')" min-width="130" prop="doc_no"></el-table-column>
              <el-table-column :label="$t('modern_trade.invoice.net_amount')" min-width="110" align="right">
                <template slot-scope="{ row }">{{ money(row.net_amount) }}</template>
              </el-table-column>
              <el-table-column :label="$t('modern_trade.invoice.paid_amount')" min-width="110" align="right">
                <template slot-scope="{ row }">{{ money(row.paid_amount) }}</template>
              </el-table-column>
              <el-table-column :label="$t('modern_trade.invoice.status')" min-width="110" align="center">
                <template slot-scope="{ row }">
                  <span class="badge" :class="invoiceStatusClass(row.status)">{{ invoiceStatusLabel(row.status) }}</span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('actions')" min-width="120" align="center">
                <template slot-scope="{ row }">
                  <base-button size="sm" type="info" @click.native="downloadInvoicePdf(row)">
                    <i class="fa fa-file-pdf"></i> PDF
                  </base-button>
                </template>
              </el-table-column>
            </el-table>
            </div>
            <h4 v-if="payments.length" class="mb-2">{{ $t("modern_trade.payment.history") }}</h4>
            <div v-if="payments.length" style="margin: 0 -1.5rem -1.5rem">
            <el-table :data="payments" header-row-class-name="thead-light" size="small" style="width: 100%">
              <el-table-column :label="$t('modern_trade.payment.paid_date')" min-width="110">
                <template slot-scope="{ row }">{{ formatDate(row.paid_date) }}</template>
              </el-table-column>
              <el-table-column :label="$t('modern_trade.payment.amount')" min-width="110" align="right">
                <template slot-scope="{ row }">{{ money(row.amount) }}</template>
              </el-table-column>
              <el-table-column :label="$t('modern_trade.payment.method')" min-width="100">
                <template slot-scope="{ row }">{{ row.method || "-" }}</template>
              </el-table-column>
              <el-table-column :label="$t('modern_trade.payment.note')" min-width="140">
                <template slot-scope="{ row }">{{ row.note || "-" }}</template>
              </el-table-column>
              <el-table-column :label="$t('modern_trade.payment.slip')" min-width="90" align="center">
                <template slot-scope="{ row }">
                  <a
                    v-if="row.slip_s3_key"
                    :href="slipUrl(row.slip_s3_key)"
                    target="_blank"
                    rel="noopener"
                  >
                    <i class="fa fa-paperclip"></i> {{ $t("modern_trade.payment.slip_view") }}
                  </a>
                  <span v-else>-</span>
                </template>
              </el-table-column>
            </el-table>
            </div>
          </template>
        </card>
        </el-tab-pane>
        </el-tabs>
      </template>
    </div>

    <!-- Delivery Dialog -->
    <el-dialog
      :title="$t('modern_trade.sale_order.create_delivery')"
      :visible.sync="showDeliveryModal"
      width="950px"
      @closed="resetDeliveryForm"
    >
      <div class="row">
        <div class="col-md-4">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.sale_order.delivery_date") }} *</label>
            <input v-model="deliveryForm.delivery_date" type="date" class="form-control" />
          </div>
        </div>
        <!-- No carrier picker here: it only ever fed the auto-created BO order and
             MT stopped creating those. The shipping team picks the carrier itself
             and keys the tracking number back onto the DO. -->
      </div>

      <!-- Lot rows (consignment / outright type 2) -->
      <div v-if="deliveryNeedsLot">
        <h4>{{ $t("modern_trade.sale_order.lot_no") }} (FEFO)</h4>
        <el-table :data="deliveryLotRows" header-row-class-name="thead-light" size="small">
          <el-table-column :label="$t('modern_trade.sale_order.product')" min-width="170">
            <template slot-scope="{ row }"><span v-if="row.product_code">{{ row.product_code }} - </span>{{ row.product_name }}</template>
          </el-table-column>
          <el-table-column label="Lot No." prop="lot_no" min-width="110"></el-table-column>
          <el-table-column :label="$t('modern_trade.sale_order.expired_date')" prop="expired_date" min-width="110"></el-table-column>
          <el-table-column :label="$t('modern_trade.sale_order.qty_received') || 'รับเข้า'" prop="qty_received" min-width="70" align="right"></el-table-column>
          <el-table-column :label="$t('modern_trade.sale_order.qty_delivered') || 'ส่งแล้ว'" prop="qty_delivered" min-width="70" align="right"></el-table-column>
          <el-table-column :label="$t('modern_trade.sale_order.available') || 'คงเหลือ'" min-width="80" align="right">
            <template slot-scope="{ row }">{{ row.available }}</template>
          </el-table-column>
          <el-table-column :label="$t('modern_trade.sale_order.qty')" min-width="110" align="center">
            <template slot-scope="{ row }">
              <input v-model.number="row.qty" type="number" min="0" :max="row.available" class="form-control form-control-sm" />
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- Product+qty rows (outright type 1/3) -->
      <div v-else>
        <h4>{{ $t("modern_trade.sale_order.items") }}</h4>
        <el-table :data="deliveryProductRows" header-row-class-name="thead-light" size="small">
          <el-table-column :label="$t('modern_trade.sale_order.product')" min-width="200">
            <template slot-scope="{ row }"><span v-if="row.product_code">{{ row.product_code }} - </span>{{ row.product_name }}</template>
          </el-table-column>
          <el-table-column :label="$t('modern_trade.sale_order.qty_delivered') || 'ส่งแล้ว'" prop="delivered" min-width="70" align="right"></el-table-column>
          <el-table-column :label="$t('modern_trade.sale_order.available') || 'คงเหลือ'" prop="remaining" min-width="80" align="right"></el-table-column>
          <el-table-column :label="$t('modern_trade.sale_order.qty')" min-width="120" align="center">
            <template slot-scope="{ row }">
              <input v-model.number="row.qty" type="number" min="0" :max="row.remaining" class="form-control form-control-sm" />
            </template>
          </el-table-column>
        </el-table>
        <!-- #243: this used to tell the operator the system does NOT create a BO
             order for these two types. Since bo-api #507 it does — on request,
             from the delivery note — and MtBoOrderGate
             (MT-BoOrderFromDelivery.go:243) excludes only shipping_type 2
             (factory). So the condition below is already the right one; it was
             the sentence that was backwards. -->
        <small v-if="canCreateBoOrderFromDelivery" class="text-muted d-block mt-2">
          <i class="fa fa-info-circle"></i>
          {{ $t("modern_trade.sale_order.shipping_team_note") }}
        </small>
      </div>

      <span slot="footer">
        <el-button @click="showDeliveryModal = false">{{ $t("close") }}</el-button>
        <el-button type="primary" :loading="creatingDelivery" @click="handleCreateDelivery">{{ $t("save") }}</el-button>
      </span>
    </el-dialog>

    <!-- round-19 Dev-B — Ship-status Dialog -->
    <el-dialog
      :title="$t('modern_trade.delivery.ship_status.dialog_title')"
      :visible.sync="showShipModal"
      width="440px"
      append-to-body
    >
      <div v-if="shipForm.row">
        <div class="form-group">
          <label class="form-control-label">{{ $t("modern_trade.delivery.ship_status.label") }}</label>
          <el-select v-model="shipForm.ship_status" class="w-100">
            <el-option :value="1" :label="$t('modern_trade.delivery.ship_status.pending')"></el-option>
            <el-option :value="2" :label="$t('modern_trade.delivery.ship_status.shipped')"></el-option>
          </el-select>
        </div>
        <div class="form-group">
          <label class="form-control-label">{{ $t("modern_trade.delivery.ship_status.tracking_number") }}</label>
          <!-- A DO with no packing order is shipped by hand, so this is the only
               place its carrier number can live — every shipping type gets the
               same editable field. -->
          <input
            v-model="shipForm.tracking_number"
            type="text"
            class="form-control"
            :placeholder="$t('modern_trade.delivery.ship_status.tracking_placeholder')"
          />
        </div>
        <!-- ห้ามลืมข้อความนี้ (issue #236 ข้อ 4): เส้นนี้ระบบไม่ตัดสต็อกให้เลย
             ถ้า DO ใบนี้มีออเดอร์อยู่แล้ว เส้นนี้ไม่ใช่ของมัน — BE ตอบ 409 has_bo_order
             จึงบอกไปตรง ๆ ว่าเลขพัสดุอยู่ที่ออเดอร์ แทนที่จะให้กด save แล้วเด้ง error -->
        <div v-if="shipLockedByOrder" class="alert alert-info py-2 mb-0">
          <i class="fa fa-info-circle"></i>
          {{ $t("modern_trade.delivery.bo_order.locked_by_order", { id: shipLockedByOrder.id }) }}
        </div>
        <div v-else class="alert alert-warning py-2 mb-0">
          <i class="fa fa-exclamation-triangle"></i>
          <strong>{{ $t("modern_trade.delivery.bo_order.manual_ship_warning") }}</strong>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="showShipModal = false">{{ $t("close") }}</el-button>
        <el-button
          type="primary"
          :loading="shipSaving"
          :disabled="!!shipLockedByOrder"
          @click="submitShipStatus"
        >{{ $t("save") }}</el-button>
      </span>
    </el-dialog>

    <!-- #504 — พรีวิวก่อนสร้างออเดอร์เข้าห้องแพ็ค -->
    <el-dialog
      :title="$t('modern_trade.delivery.bo_order.dialog_title')"
      :visible.sync="showBoOrderModal"
      width="760px"
      append-to-body
      @closed="resetBoOrderForm"
    >
      <div v-loading="boOrderLoading">
        <div v-if="boOrderPreview">
          <!-- 1. ผู้รับ — ค่าที่จะถูกเขียนลงออเดอร์จริง -->
          <div class="mb-3">
            <label class="form-control-label">{{ $t("modern_trade.delivery.bo_order.recipient") }}</label>
            <div v-if="boOrderRecipient.company_name || boOrderRecipient.address || boOrderRecipient.phone">
              <div><strong>{{ boOrderRecipient.company_name || "-" }}</strong></div>
              <div class="text-muted small">{{ boOrderRecipient.address || "-" }}</div>
              <div class="text-muted small">
                {{ $t("modern_trade.delivery.bo_order.recipient_phone") }}: {{ boOrderRecipient.phone || "-" }}
              </div>
            </div>
            <div v-else class="text-muted small">{{ $t("modern_trade.delivery.bo_order.recipient_empty") }}</div>
          </div>

          <!-- 2. บริษัทขนส่ง — บังคับกรอก เว้นแต่ BE บอกว่าล็อกไว้แล้ว (ฝากส่ง = MDF Express) -->
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.delivery.bo_order.shipping_provider") }} *</label>
            <div v-if="boOrderProviderLocked">
              <input
                type="text"
                class="form-control"
                readonly
                :value="boOrderShipping.provider_name || providerName(boOrderShipping.provider_id)"
              />
              <small class="text-muted">{{ $t("modern_trade.delivery.bo_order.shipping_provider_locked") }}</small>
            </div>
            <el-select
              v-else
              v-model="boOrderForm.provider_id"
              class="w-100"
              :placeholder="$t('modern_trade.delivery.bo_order.shipping_provider_placeholder')"
            >
              <el-option
                v-for="p in shippingProviders"
                :key="p.id"
                :value="p.id"
                :label="p.name"
              ></el-option>
            </el-select>
          </div>

          <!-- 3. ตารางของที่จะถูกกัน — แสดง lines[] ของ BE ตรง ๆ ห้ามคำนวณเอง
               ตัวเลขนี้คือตัวเลขที่จะถูกหักจากสต็อกที่ประกาศบนแพลตฟอร์ม
               พรีวิวที่โกหกอันตรายกว่าไม่มีพรีวิว -->
          <label class="form-control-label">{{ $t("modern_trade.delivery.bo_order.lines_title") }}</label>
          <el-table :data="boOrderLines" header-row-class-name="thead-light" size="small" empty-text="-">
            <el-table-column :label="$t('modern_trade.delivery.bo_order.col_product')" min-width="200">
              <template slot-scope="{ row }">{{ row.name || "#" + row.wms_product_id }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('modern_trade.delivery.bo_order.col_barcode')"
              prop="barcode"
              min-width="130"
            ></el-table-column>
            <el-table-column :label="$t('modern_trade.delivery.bo_order.col_mt_codes')" min-width="140">
              <template slot-scope="{ row }">{{ (row.mt_product_codes || []).join(", ") || "-" }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.delivery.bo_order.col_qty')" min-width="80" align="right">
              <template slot-scope="{ row }"><strong>{{ row.quantity }}</strong></template>
            </el-table-column>
          </el-table>
          <small class="text-muted d-block mt-1">{{ $t("modern_trade.delivery.bo_order.lines_note") }}</small>

          <!-- 4. เหตุผลที่กดไม่ได้ (ถ้ามี) — ข้อความจาก BE ตรง ๆ เพราะเป็นตัวเดียวที่รู้ว่า
               รหัสไหน/บรรทัดไหนที่ยังไม่ผ่าน -->
          <div v-if="!boOrderPreview.can_create" class="alert alert-danger py-2 mt-3 mb-0">
            <i class="fa fa-ban"></i> {{ doReasonTextOf(boOrderPreview) }}
          </div>

          <!-- 5. คำเตือนเหนือปุ่มยืนยัน -->
          <div v-else class="alert alert-warning py-2 mt-3 mb-0">
            <i class="fa fa-exclamation-triangle"></i>
            <strong>{{ $t("modern_trade.delivery.bo_order.confirm_warning") }}</strong>
          </div>

          <!-- 6. บรรทัดที่ยังไม่ครบ (#240) — มาจาก missing_lots ของ BE ล้วน ๆ:
               line เป็นฐาน 1 อ้างตำแหน่งจริงในใบส่งของ ⇒ เจ้าหน้าที่ไม่ต้องไล่นับแถวเอง
               และหน้าจอไม่ต้อง parse ข้อความไทยใน reason_message ซึ่งจะพังทันทีที่ BE
               แก้คำ · ตารางนี้เป็น "ส่วนเพิ่ม" ของ alert ข้างบนเท่านั้น ไม่ได้แทนที่:
               BE รุ่นที่ยังไม่ส่ง missing_lots มา ผู้ใช้ต้องยังเห็น reason_message เหมือนเดิม -->
          <div v-if="boOrderMissingLots.length" class="mt-3 mt-missing-lots">
            <label class="form-control-label text-danger mb-1">
              {{ $t("modern_trade.delivery.bo_order.missing_lots_title") }}
              ({{ $t("modern_trade.delivery.bo_order.missing_lots_count", { count: boOrderMissingLots.length }) }})
            </label>
            <el-table
              :data="boOrderMissingLots"
              header-row-class-name="thead-light"
              size="small"
              :row-class-name="missingLotRowClass"
            >
              <el-table-column
                :label="$t('modern_trade.delivery.bo_order.col_line')"
                min-width="80"
                align="right"
              >
                <template slot-scope="{ row }"><strong>{{ row.line }}</strong></template>
              </el-table-column>
              <!-- เลขบรรทัดอย่างเดียวไม่บอกว่าต้องไปแก้สินค้าตัวไหน — แปลงเป็นรหัสสินค้า
                   จาก delivery_items ที่ saleOrderGet ส่งมา (ดู boOrderDeliveryLines) -->
              <el-table-column :label="$t('modern_trade.delivery.bo_order.col_product')" min-width="200">
                <template slot-scope="{ row }">
                  <span v-if="missingLotProduct(row)">{{ missingLotProduct(row) }}</span>
                  <span v-else class="text-muted">-</span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('modern_trade.delivery.bo_order.col_lot')" min-width="150">
                <template slot-scope="{ row }">
                  <span v-if="row.lot_no">{{ row.lot_no }}</span>
                  <span v-else class="text-danger">{{ $t("modern_trade.delivery.bo_order.lot_empty") }}</span>
                </template>
              </el-table-column>
              <!-- ขาด LOT กับ มี LOT แต่ไม่มีวันหมดอายุ ต้องแยกให้เห็น: สองอย่างนี้
                   เจ้าหน้าที่ต้องไปแก้คนละที่ (บรรทัดในใบส่งของ vs ล็อตที่รับเข้า) -->
              <el-table-column :label="$t('modern_trade.delivery.bo_order.col_problem')" min-width="330">
                <template slot-scope="{ row }">
                  <div v-if="row.missing_lot">
                    <span class="badge badge-danger">{{ $t("modern_trade.delivery.bo_order.problem_missing_lot") }}</span>
                    <span class="small text-muted ml-1">{{ $t("modern_trade.delivery.bo_order.fix_missing_lot") }}</span>
                  </div>
                  <div v-if="row.missing_expired_date">
                    <span class="badge badge-warning">{{ $t("modern_trade.delivery.bo_order.problem_missing_expire") }}</span>
                    <span class="small text-muted ml-1">{{ $t("modern_trade.delivery.bo_order.fix_missing_expire") }}</span>
                  </div>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
        <div v-else class="text-center text-muted py-4">{{ $t("modern_trade.delivery.bo_order.loading") }}</div>
      </div>
      <span slot="footer">
        <el-button @click="showBoOrderModal = false">{{ $t("close") }}</el-button>
        <el-button
          type="primary"
          :loading="boOrderSubmitting"
          :disabled="!boOrderCanSubmit"
          @click="submitBoOrder"
        >{{ $t("modern_trade.delivery.bo_order.confirm_action") }}</el-button>
      </span>
    </el-dialog>

    <!-- Payment Dialog — บันทึกรับเงิน (ระบบออกใบกำกับภาษีอัตโนมัติ) -->
    <el-dialog
      :title="$t('modern_trade.payment.record')"
      :visible.sync="showPaymentModal"
      width="480px"
      append-to-body
    >
      <div class="form-group">
        <label class="form-control-label">{{ $t("modern_trade.payment.amount") }} *</label>
        <!-- #154: cap = ยอดคงค้างของใบกำกับที่ยัง live. ตั้งใจใช้ <input> ธรรมดา ไม่ใช่
             el-input-number เพราะ el-input-number จะ clamp ค่าที่เกิน :max แล้ว emit
             กลับเงียบ ๆ (ผู้ใช้ไม่รู้ว่าตัวเลขถูกเปลี่ยน) — ที่นี่เราต้องการ error ชัด ๆ แทน -->
        <input
          v-model.number="paymentForm.amount"
          type="number"
          min="0"
          :max="outstanding"
          step="0.01"
          class="form-control"
          :class="{ 'is-invalid': amountExceedsOutstanding }"
          @focus="$event.target.select()"
        />
        <small v-if="amountExceedsOutstanding" class="text-danger d-block mt-1">
          {{ $t("modern_trade.payment.exceeds_outstanding", { amount: money(outstanding) }) }}
        </small>
        <small v-else class="text-muted d-block mt-1">
          {{ $t("modern_trade.payment.outstanding") }}: {{ money(outstanding) }}
        </small>
      </div>
      <div class="form-group">
        <label class="form-control-label">{{ $t("modern_trade.payment.paid_date") }} *</label>
        <input v-model="paymentForm.paid_date" type="date" class="form-control" />
      </div>
      <div class="form-group">
        <label class="form-control-label">{{ $t("modern_trade.payment.method") }}</label>
        <el-select v-model="paymentForm.method" class="w-100" clearable>
          <el-option :label="$t('modern_trade.payment.method_transfer')" value="โอนเงิน"></el-option>
          <el-option :label="$t('modern_trade.payment.method_cash')" value="เงินสด"></el-option>
          <el-option :label="$t('modern_trade.payment.method_cheque')" value="เช็ค"></el-option>
          <el-option :label="$t('modern_trade.payment.method_other')" value="อื่น ๆ"></el-option>
        </el-select>
      </div>
      <div class="form-group">
        <label class="form-control-label">{{ $t("modern_trade.payment.note") }}</label>
        <input v-model="paymentForm.note" type="text" class="form-control" />
      </div>
      <div class="form-group">
        <label class="form-control-label">{{ $t("modern_trade.payment.slip") }}</label>
        <div>
          <input
            ref="slipInput"
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            style="display:none"
            @change="onSlipPicked"
          />
          <el-button size="small" :loading="slipUploading" @click="$refs.slipInput.click()">
            <i class="fa fa-upload"></i> {{ $t("modern_trade.payment.slip_choose") }}
          </el-button>
          <span v-if="paymentForm.slip_object_key" class="ml-2">
            <a :href="paymentForm.slip_cdn_url" target="_blank" rel="noopener">
              <i class="fa fa-paperclip"></i> {{ $t("modern_trade.payment.slip_view") }}
            </a>
            <i class="fa fa-times text-danger ml-2" style="cursor:pointer" @click="clearSlip"></i>
          </span>
        </div>
        <small class="text-muted d-block mt-1">{{ $t("modern_trade.payment.slip_hint") }}</small>
      </div>
      <div class="alert alert-info mb-0">
        <i class="fa fa-info-circle"></i> {{ $t("modern_trade.payment.history_hint") }}
      </div>
      <span slot="footer">
        <el-button @click="showPaymentModal = false">{{ $t("close") }}</el-button>
        <el-button
          type="primary"
          :loading="paymentSaving"
          :disabled="slipUploading"
          @click="submitPayment"
        >{{ $t("save") }}</el-button>
      </span>
    </el-dialog>

    <!-- Shared product detail popup (#164) -->
    <product-detail-popup
      :visible.sync="showProductPopup"
      :product="productPopup"
    ></product-detail-popup>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Dialog, Button, Tabs, TabPane, DatePicker } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import ProductDetailPopup from "@/components/Modern/ProductDetailPopup";
import mtStatusColor from "@/mixins/mtStatusColor";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { ModernTradeService } from "@/resource";
import { canIssueFromDelivery, showSource3IssueButton, invoiceErrorKey } from "@/util/mt/invoiceUi";
import { mtProductLabel } from "@/util/mt/productLabel";
import {
  validate as validateLotRows,
  minExpiry,
  receiveProgress,
  manualPayload,
  rowErrorsFromServer,
  progressRows,
  lotErrorKey,
  receiveOpen,
} from "@/util/mt/lotForm";
import { saleOrderErrorKey } from "@/util/mt/soDraft";
import {
  canCreateDelivery,
  deliverableLots,
  productRemainingRows,
  outstandingRows,
  canCancelDelivery,
} from "@/util/mt/deliveryRemaining";
import { localToday } from "@/util/mt/expiryReturn";

// #243 — the three values MtBoOrderGate's first clause tests
// (mdf-bo-api/controllers/MT-BoOrderFromDelivery.go:243, constants at
// MT-StructV2.go:306-318). Named here so the note's condition reads as "the
// cases the gate rules out", which is what it has to stay in step with.
const MT_SHIPPING_TYPE_FACTORY = 2;
const MT_SO_STATUS_CLOSED = 6;
const MT_SO_STATUS_CANCELLED = 9;

export default {
  name: "MtSaleOrderDetail",
  mixins: [mtStatusColor],
  components: {
    RouteBreadCrumb,
    ProductDetailPopup,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Dialog.name]: Dialog,
    [Button.name]: Button,
    [Tabs.name]: Tabs,
    [TabPane.name]: TabPane,
    [DatePicker.name]: DatePicker,
  },
  watch: {
    // Server row errors point at row positions; any edit makes them stale.
    lotRows: {
      deep: true,
      handler() {
        if (Object.keys(this.lotServerErrors).length) this.lotServerErrors = {};
      },
    },
  },
  computed: {
    ...mapGetters({
      isAccessModernTrade: "Auth/isAccessModernTrade",
      isEditModernTrade: "Auth/isEditModernTrade",
    }),
    // #303: ปิดใบ (6) = สถานะสุดท้าย. BE บล็อกทุก mutation ของใบสั่งขายที่ปิดแล้ว
    // (409 code `sale_order_closed`) — FE จึงซ่อนปุ่มที่เขียนข้อมูลทั้งหมด เหลือแค่ดู/พิมพ์.
    isClosed() {
      return !!this.so && this.so.status === 6;
    },
    isCancelled() {
      return !!this.so && this.so.status === 9;
    },
    // ประตูเดียวสำหรับปุ่ม/ฟอร์มที่ทำ mutation ทุกตัวในหน้านี้.
    canMutate() {
      return this.isEditModernTrade && !!this.so && !this.isClosed && !this.isCancelled;
    },
    canEdit() {
      return this.canMutate && this.so.status === 1;
    },
    isConsignment() {
      return this.so && this.so.sale_type === 1;
    },
    isOutright() {
      return this.so && this.so.sale_type === 2;
    },
    // §3A: ปุ่ม "ออกใบจากเงินที่รับ" (source 3) หายเมื่อ SO เริ่มใช้ใบตามใบส่งของแล้ว
    // 2026-11-04: ขายขาดที่ไม่ใช่ legacy (ไม่มีใบ source 3 อยู่แล้ว) ไม่แสดงปุ่มนี้เลย — ออกใบจากแถวใบส่งของ
    showSource3Button() {
      return showSource3IssueButton(this.paymentCap, this.deliveries, this.so && this.so.sale_type);
    },
    // MT tracks lots for consignment (all shipping) and outright-from-factory (type 2).
    needsLot() {
      if (!this.so) return false;
      return this.isConsignment || (this.isOutright && this.so.shipping_type === 2);
    },
    // #243 — whether a delivery note from THIS sale order can produce a BO order
    // (pack-room order) on request. Since bo-api #507 the delivery note grew a
    // "สร้างออเดอร์เข้าห้องแพ็ค" button, and MtBoOrderGate
    // (MT-BoOrderFromDelivery.go:243) rules out exactly: factory shipment (2),
    // a cancelled sale order (9), and a closed one (6). Everything after that
    // clause in the gate is per-delivery-note (already created, stock cut,
    // shipped by hand, settings, WMS map, lot/expiry) and cannot be known here —
    // the note only promises the door exists, and the gate still has the last
    // word on the delivery note itself.
    //
    // Written as "not the excluded set" rather than "=== 1 || === 3" on purpose:
    // the previous form silently stopped applying to a new shipping type, which
    // is how the stale wording survived #507 unnoticed in the first place.
    canCreateBoOrderFromDelivery() {
      if (!this.so) return false;
      if (this.so.shipping_type === MT_SHIPPING_TYPE_FACTORY) return false;
      return this.so.status !== MT_SO_STATUS_CLOSED && this.so.status !== MT_SO_STATUS_CANCELLED;
    },
    // Legacy only: sale orders created while MT still opened BO orders keep that
    // link and must stay viewable. Guarded on a real id so an empty `{}` from the
    // API never renders a bare "#" with a floating heading.
    hasBoOrder() {
      return !!(this.boOrder && (this.boOrder.id || this.boOrder.order_id));
    },
    showProductionCard() {
      return this.so && this.needsLot && this.so.status !== 1 && this.so.status !== 9;
    },
    showGoodsCard() {
      return this.so && this.needsLot && this.so.status !== 1 && this.so.status !== 9;
    },
    // Nov-5 §4: receipt progress from GET /goods/progress (null = not loaded / old BE).
    goodsProgressState() {
      return receiveProgress(this.goodsProgress);
    },
    // Lot form + Excel wizard: SO status 2..5 while anything remains to
    // receive (partial factory delivery: a delivery note moves the SO to 5).
    // Without progress data (old BE) fall back to the old 2–3 rule.
    canReceiveGoods() {
      if (!this.canMutate) return false;
      return receiveOpen(this.so.status, this.goodsProgress);
    },
    lotProducts() {
      return this.goodsProgress || [];
    },
    lotMinExpiry() {
      return minExpiry(this.lotToday);
    },
    lotPickerOptions() {
      const min = this.lotMinExpiry;
      const pad = n => String(n).padStart(2, "0");
      return {
        disabledDate: d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` < min,
      };
    },
    lotCheck() {
      return validateLotRows(this.lotProducts, this.lotRows, this.lotToday);
    },
    showDeliveryCard() {
      return this.so && this.so.status !== 1 && this.so.status !== 9;
    },
    deliveryNeedsLot() {
      return this.needsLot;
    },
    // Owner 2026-11-04: several DOs per SO (factory delivers in parts) — offer
    // the button for the first DO, then while anything is still deliverable.
    showCreateDelivery() {
      return canCreateDelivery({
        canMutate: this.canMutate,
        status: this.so && this.so.status,
        needsLot: this.deliveryNeedsLot,
        deliveries: this.deliveries,
        deliveryItems: this.deliveryItems,
        receivedLots: this.receivedLots,
        items: this.items,
      });
    },
    // #154: เพดานยอดรับเงิน = ยอดคงค้างของใบกำกับที่ยัง live (net − paid).
    // อันดับ 1: `payment_cap` จาก BE (GET /sale-orders/:id, helper
    // MtSaleOrderPaymentOutstanding) — คำนวณจากยอด**ที่ส่งของแล้ว**จริง (+VAT −bill
    // discount) ไม่ใช่ so.net_amount เต็มยอด ถูกต้องแม้กรณีส่งของบางส่วน.
    // Fallback (BE เก่ายังไม่มี payment_cap): เดิมใช้ so.net_amount ซึ่ง**ผิด**เมื่อส่งของ
    // บางส่วน (จะโชว์เพดานสูงเกินจริง) — คงไว้เป็น best-effort เท่านั้น
    // computed เพื่อให้ค่าอัปเดตเองหลัง loadDetail() ทุกครั้ง (จ่ายบางส่วนแล้วเพดานลดลง)
    outstanding() {
      if (this.paymentCap) {
        return Math.max(0, Math.round(Number(this.paymentCap.outstanding || 0) * 100) / 100);
      }
      if (!this.so) return 0;
      const inv = this.invoices.find(i => i.status !== 9);
      const base = inv
        ? Number(inv.net_amount || 0) - Number(inv.paid_amount || 0)
        : Number(this.so.net_amount || 0);
      return Math.max(0, Math.round(base * 100) / 100);
    },
    // #504 — พรีวิวที่เปิดอยู่ใน modal (ก้อนดิบจาก BE)
    boOrderPreview() {
      return this.boOrderForm.preview || null;
    },
    boOrderRecipient() {
      return (this.boOrderPreview && this.boOrderPreview.recipient) || {};
    },
    boOrderShipping() {
      return (this.boOrderPreview && this.boOrderPreview.shipping) || {};
    },
    boOrderProviderLocked() {
      return !!this.boOrderShipping.provider_locked;
    },
    // ตารางพรีวิวต้องสะท้อน lines[] ของ BE ตรง ๆ (issue #236 AC2: แก้ค่าที่ BE ส่งมา
    // ตารางต้องเปลี่ยนตาม) — ไม่มีการรวม/คูณ/กรองใด ๆ ที่นี่
    boOrderLines() {
      return (this.boOrderPreview && this.boOrderPreview.lines) || [];
    },
    // #240 — บรรทัดที่ LOT/วันหมดอายุยังไม่ครบ
    // พรีวิวที่เพิ่งดึงมาชนะเสมอเพราะสดกว่า *แม้จะเป็น []* — BE normalize nil เป็น []
    // ให้แล้ว (MT-BoOrderFromDelivery.go:978-979) ⇒ มี field นี้ = พรีวิวรอบนี้ตอบเรื่อง
    // LOT ครบแล้ว ถ้าไปเช็ค length แทนการเช็คว่ามี field ผู้ใช้ที่เพิ่งแก้ LOT จนครบ
    // จะยังเห็นตารางเก่าของครั้งที่ถูกปฏิเสธค้างอยู่ ทั้งที่ BE ไม่ได้ว่าอะไรแล้ว
    // ของที่ติดมากับ 409/400 เป็นตัวสำรองของสองกรณีจริง: BE รุ่นที่ preview ยังไม่ส่ง
    // field นี้ และตอนที่ดึงพรีวิวใหม่หลังโดนปฏิเสธไม่สำเร็จ
    boOrderMissingLots() {
      const preview = this.boOrderPreview;
      if (preview && Array.isArray(preview.missing_lots)) {
        return this.normalizeMissingLots(preview.missing_lots);
      }
      return this.normalizeMissingLots(this.boOrderRejection && this.boOrderRejection.missing_lots);
    },
    // #240 nit — บรรทัดของใบส่งของใบที่เปิด modal อยู่ เรียงตามที่ BE ส่งมา
    // ใช้แปลง "บรรทัดที่ N" ให้เป็นรหัส/ชื่อสินค้า: เลขบรรทัดอย่างเดียวไม่บอกว่าต้องไป
    // แก้สินค้าตัวไหน ⇒ เจ้าหน้าที่ต้องเปิดใบส่งของอีกหน้าต่างมานั่งไล่นับ
    //
    // index ตรงกันได้เพราะสองฝั่งถามคำถามเดียวกันเป๊ะ ๆ: bo_mt_delivery_item ของ
    // delivery_id เดียวกัน ORDER BY id ASC ไม่กรองแถวไหนออกทั้งคู่
    // (MT-SaleOrder.go:630-636 ↔ MT-BoOrderFromDelivery.go:473-476) และ
    // MtBoOrderMissingLot.Line เป็นตำแหน่งสัมบูรณ์ในลิสต์นั้น (แถว qty<=0 ถูกข้าม
    // แต่ยังนับ index อยู่ MT-BoOrderFromDelivery.go:700-723)
    // ⚠️ ห้ามเอาเลขบรรทัดนี้ไป index ใส่ preview.lines[] เด็ดขาด — อันนั้นยุบสามชั้น
    // (ProductID → wms_product_id → ตัด qty<=0 ทิ้ง) ตำแหน่งไม่ตรงกัน
    boOrderDeliveryLines() {
      const doID = Number((this.boOrderForm.delivery && this.boOrderForm.delivery.id) || 0);
      if (!doID) return [];
      return this.deliveryItems.filter(x => x && Number(x.delivery_id) === doID);
    },
    boOrderCanSubmit() {
      if (!this.boOrderPreview || !this.boOrderPreview.can_create) return false;
      if (this.boOrderProviderLocked) return true;
      return !!this.boOrderForm.provider_id;
    },
    // ใบส่งของที่มีออเดอร์อยู่แล้ว: เลขพัสดุเป็นของออเดอร์ BE ตอบ 409 has_bo_order
    shipLockedByOrder() {
      return this.shipForm.row ? this.doLiveOrder(this.shipForm.row) : null;
    },
    amountExceedsOutstanding() {
      const amt = Number(this.paymentForm.amount);
      if (!amt || amt <= 0) return false;
      // epsilon 0.005 กันเศษทศนิยมจากการปัดของ BE/FE
      return amt > this.outstanding + 0.005;
    },
  },
  data() {
    return {
      // receive date for the lot form's 1-year rule — refreshed on every
      // activation and before save so a tab left open past midnight is not stale
      lotToday: localToday(),
      loading: false,
      activeTab: "items",
      // round-19.1 deep-link: การ์ดที่กำลังไฮไลต์ชั่วคราวหลังเด้งจากช่องค้นเอกสาร
      focusHighlight: "",
      // shared product detail popup
      showProductPopup: false,
      productPopup: null,
      so: null,
      items: [],
      productionOrders: [],
      deliveries: [],
      // บรรทัดของใบส่งของทุกใบใน SO นี้ — เรียงตามที่ BE ส่งมา (id ASC) ห้ามเรียงใหม่
      deliveryItems: [],
      invoices: [],
      payments: [],
      boOrder: null,
      receivedLots: [],
      // #154: BE payment_cap { invoice_id, net_amount, paid_amount, outstanding, can_receive }
      // (invoice_id=0 = ยังไม่มี IV). null จนกว่า loadDetail() จะเซ็ต — คำนวณจาก outstanding computed
      paymentCap: null,
      // action states
      downloadingPo: false,
      confirming: false,
      cancelling: false,
      closing: false,
      cancellingDeliveryId: null,
      genningMo: false,
      showPaymentModal: false,
      paymentSaving: false,
      issuingInvoice: false,
      // §3A: id ของใบส่งของที่กำลังออกใบกำกับ (null = ว่าง) — กันกดซ้อนทุกแถว
      issuingDeliveryId: null,
      slipUploading: false,
      paymentForm: { amount: null, paid_date: "", method: "", note: "", slip_object_key: "", slip_cdn_url: "" },
      creatingDelivery: false,
      // Nov-5 §4 lot form: progress rows + entered lot rows (flat, form order)
      goodsProgress: null,
      lotRows: [],
      lotRowSeq: 0,
      lotServerErrors: {},
      savingLots: false,
      // goods import wizard
      wizardStep: 1,
      goodsFile: null,
      validating: false,
      confirmingGoods: false,
      batch: { id: null, row_total: 0, row_ok: 0, row_error: 0 },
      previewRows: [],
      // delivery dialog
      showDeliveryModal: false,
      deliveryForm: { delivery_date: "" },
      deliveryLotRows: [],
      deliveryProductRows: [],
      // round-19 Dev-B — ship-status dialog
      showShipModal: false,
      shipSaving: false,
      shipForm: { row: null, ship_status: 1, tracking_number: "" },
      // #504 — "สร้างออเดอร์เข้าห้องแพ็ค" ต่อใบส่งของ
      // boOrderPreviews: delivery_id -> payload ของ GET .../bo-order/preview
      // เก็บทั้งก้อนที่ BE ส่งมา ไม่แกะเป็นบูลีนของตัวเอง เพราะหน้าจอไม่ได้เป็นคนตัดสิน
      // ว่ากดได้ไหม และ reason_message เป็นข้อความเดียวที่รู้ว่ารหัสไหนตกเงื่อนไข
      boOrderPreviews: {},
      // ตัวนับรอบของ loadBoOrderPreviews() — กันผลของรอบเก่าเขียนทับรอบใหม่
      boOrderPreviewToken: 0,
      showBoOrderModal: false,
      boOrderLoading: false,
      boOrderSubmitting: false,
      boOrderForm: { delivery: null, preview: null, provider_id: null },
      // #240 — ก้อน missing_lots ที่ติดมากับ 409/400 ของครั้งล่าสุดที่กดยืนยัน
      // (go-core ใส่ไว้ใน Data[0] ของ body ที่ error ไม่ใช่แค่ข้อความใน Cause)
      boOrderRejection: null,
      // รายชื่อบริษัทขนส่งสำหรับ dropdown — ตายตัวในหน้านี้ ห้ามไปดึงจาก API
      //
      // สัญญาของ preview ให้มาแค่ provider ที่ถูกล็อก ไม่ได้ให้ "รายการที่เลือกได้"
      // แหล่งข้อมูลจริง (GET /api/v1/shipping-config/options) กั้นด้วยสิทธิ์
      // shipping_config ซึ่งเป็นของ SuperAdmin เท่านั้น (field 24) และประตูนั้น
      // ตอบ 403 access_denied (bo-api #937 — เดิมตอบ 401 echo.ErrUnauthorized ซึ่ง
      // interceptor ที่ src/resource/http.js ลบคุกกี้แล้วเด้งไป /login ⇒ เจ้าหน้าที่ MT
      // ทุกคนที่ไม่ใช่ SuperAdmin ถูก logout ตั้งแต่เปิดหน้านี้) ตอนนี้ไม่ logout แล้ว
      // แต่ interceptor ยังเด้งหน้าต่าง "ไม่มีสิทธิ์" ทุกครั้งที่เปิดหน้า และ try/catch
      // ช่วยไม่ได้ เพราะ interceptor ทำงานก่อน catch เสมอ
      //
      // ⇒ วิธีเดียวที่ปลอดภัยคือไม่ยิงไปที่ประตูนั้นตั้งแต่แรก รายการด้านล่างจึงเป็น
      // รายการจริงของหน้านี้ ชุดเดียวกับที่หน้าห้องแพ็คใช้ (PackingTables.vue
      // ShippingProviderTable) ซึ่งตรงกับ const.go:222-223 ฝั่ง BO
      // เพิ่มบริษัทขนส่งใหม่เมื่อไหร่ ต้องมาเติมที่นี่ด้วยเหมือนหน้าห้องแพ็ค
      // ⚠ id + ชื่อ ต้องตรงกับ bo-api `SHIPPING_PROVIDERS`
      // (controllers/BO-ShippingProvider.go:14-23) ซึ่งเป็นตัวที่ BE ใช้แปลง id เป็น
      // ชื่อจริงลง bo_orders.shipping_provider_name ผ่าน ShippingProviderGetNameByID
      // (:55) และตรงกับค่าคงที่ ShippingProvider* ใน controllers/const.go:217-226
      // ชื่อที่นี่จึงคัดลอกมาตรงตัวอักษร เพื่อให้สิ่งที่เจ้าหน้าที่เห็นตอนเลือก
      // เท่ากับสิ่งที่จะไปโผล่บนออเดอร์
      //
      // ⚠ อย่าลอกรายการจาก ShippingForm.vue / ShippingAdminForm.vue /
      // ShippingReturnForm.vue / ExpressForm.vue — ทั้ง 4 หน้านั้นถือรายการเก่า
      // 5 บรรทัดที่ระบุ id 5 = "มาดามฟิน เอ็กซ์เพรส" ซึ่งไม่ตรงกับ BE (id 5 = DHL,
      // มาดามฟิน คือ id 6) เลือกผิดที่นี่ = พัสดุไปผูกกับขนส่งคนละเจ้า
      //
      // ต้อง hardcode เพราะ SHIPPING_PROVIDERS ไม่มี route ให้เรียก
      // (ShippingProviderList ไม่ถูก register ใน routes ไฟล์ไหนเลย) และ
      // /api/v1/shipping-config/options ที่มีรายการนี้ กั้นด้วยสิทธิ์ SuperAdmin
      // เพิ่มบริษัทขนส่งใหม่เมื่อไหร่ ต้องมาเติมที่นี่ด้วย
      //
      // 3 (นินจาแวน) / 4 (แฟลช เอ็กซ์เพรส) / 8 (Flash Express) ไม่ใส่ — เลิกใช้แล้ว
      // เหมือนที่หน้าห้องแพ็คตัดออก (PackingTables.vue)
      shippingProviders: [
        { id: 1, name: "ไปรษณีย์ไทย" },
        { id: 2, name: "เคอรี่ เอ็กซ์เพรส" },
        { id: 5, name: "DHL" },
        { id: 6, name: "มาดามฟิน เอ็กซ์เพรส" },
        { id: 7, name: "SPX" },
        // 9 = ฝากไปกับรถบริษัท ได้เลขพัสดุ SPD… ทันทีตอนสร้างออเดอร์ (spec §3.1)
        { id: 9, name: "ขนส่งพิเศษ" },
      ],
    };
  },
  async mounted() {
    await this.loadDetail();
    this.applyDeepLinkFocus();
  },
  // This page is kept alive per taskbar tab. Coming back from the PO editor
  // (save draft / confirm) must not show the pre-edit order, so reload on
  // every re-activation (the first activation follows mounted, which loads).
  activated() {
    this.refreshLotToday();
    if (this.activatedOnce) this.loadDetail();
    this.activatedOnce = true;
  },
  beforeDestroy() {
    this.cancelPendingBatch();
  },
  methods: {
    // Return to the list via history so its saved search state (query) restores.
    goEdit() {
      this.$router.push({ name: "MtSaleOrderEdit", params: { id: String(this.$route.params.id) } });
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push({ name: "MtSaleOrders" });
      }
    },
    // round-19.1 deep-link: MtDocSearchBox ส่ง ?focus=so|po|mo|do (+doc). แม็พไปแท็บที่
    // เกี่ยว (mo/do → คลังสินค้า, iv/payment → การเงิน, ที่เหลือ → รายการ) แล้วเลื่อน+ไฮไลต์
    // การ์ดนั้น ~2 วิ. เรียกครั้งเดียวหลังโหลดข้อมูลเสร็จ (mounted) — mutation อื่นไม่ทริกเกอร์.
    applyDeepLinkFocus() {
      const focus = this.$route.query.focus;
      if (!focus || !this.so) return;
      let tab = "items";
      let ref = "headerCard";
      switch (focus) {
        case "mo": tab = "warehouse"; ref = "productionCard"; break;
        case "do": tab = "warehouse"; ref = "deliveryCard"; break;
        case "iv":
        case "invoice":
        case "payment": tab = "finance"; ref = "financeCard"; break;
        default: tab = "items"; ref = "headerCard"; break; // so, po
      }
      this.activeTab = tab;
      // lazy tab-pane ต้องรอ render ก่อน (nextTick อย่างเดียวไม่พอบางครั้ง) — หน่วงสั้น ๆ
      this.$nextTick(() => {
        setTimeout(() => {
          const comp = this.$refs[ref];
          const el = comp && comp.$el ? comp.$el : null;
          if (el && el.scrollIntoView) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
          this.focusHighlight = ref;
          setTimeout(() => { this.focusHighlight = ""; }, 2000);
        }, 200);
      });
    },
    // #183: open the full-screen print-set preview page instead of downloading.
    // ไอคอน PDF ข้างเลข PO = เอกสาร "ใบสั่งขาย" (ไฟล์ PO ที่ลูกค้าแนบมาอยู่ที่ปุ่มคลิปหนีบข้าง ๆ)
    // ชุดพิมพ์ของ BE ไม่มีชนิด "po" — ดู DOC_TYPE_ORDER ใน MtPrintPreview.vue
    openPoPdf() {
      this.goPrintPreview("so");
    },
    // docType = เอกสารที่ผู้ใช้กดมา ("so" | "invoice" | "delivery" | "production") ส่งไปให้หน้า
    // preview ติ๊กเป็นค่าเริ่มต้น; ไม่ส่ง = ปล่อยให้หน้า preview ใช้ค่าเริ่มต้นเดิม ("so")
    goPrintPreview(docType) {
      const query = { so_no: (this.so && this.so.so_no) || "" };
      if (docType) query.doc = docType;
      this.$router.push({
        name: "MtPrintPreview",
        params: { soId: String(this.$route.params.id) },
        query,
      });
    },
    openProductPopup(row) {
      // #200: BE sale-order items carry wms_name / wms_man_pn / wms_barcode
      // (resolved WMS product) alongside the legacy sku_code/man_pn/barcode
      // spellings — pass both through, the popup falls back in priority order.
      this.productPopup = {
        product_name: row.product_name,
        wms_name: row.wms_name || row.wms_product_name,
        wms_man_pn: row.wms_man_pn,
        sku_code: row.sku_code || row.man_pn,
        wms_barcode: row.wms_barcode,
        barcode: row.barcode,
        code: row.product_code,
        wms_linked: row.wms_linked,
        wms_product_id: row.wms_product_id,
      };
      this.showProductPopup = true;
    },
    async loadDetail() {
      this.loading = true;
      try {
        const res = await ModernTradeService.saleOrderGet(this.$route.params.id);
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        const so = d.sale_order || d.so || null;
        if (so) {
          this.so = Object.assign({}, so, {
            company_name: (d.company && d.company.name) || so.company_name,
          });
        } else {
          this.so = null;
        }
        this.items = (d.items || []).map(x => Object.assign({}, x));
        this.productionOrders = d.production_orders || d.mos || [];
        this.deliveries = d.deliveries || d.dos || [];
        // #240 nit — บรรทัดของใบส่งของทุกใบใน SO นี้ (MT-SaleOrder.go:678 ส่งมาให้อยู่แล้ว)
        // ใช้แปลงเลขบรรทัดใน missing_lots เป็นรหัสสินค้า ดู boOrderDeliveryLines()
        this.deliveryItems = d.delivery_items || [];
        this.invoices = d.invoices || [];
        this.payments = d.payments || [];
        this.boOrder = d.bo_order || null;
        // #154: authoritative payment cap from BE (MtSaleOrderPaymentOutstanding) —
        // accounts for partial delivery (+VAT −bill discount), unlike so.net_amount.
        this.paymentCap = d.payment_cap || null;
        // BE contract (saleOrderGet.received_lots, FEFO-sorted):
        // { product_id, product_code, product_name, lot_no, expired_date,
        //   qty_received, qty_delivered, qty_available }
        this.receivedLots = (d.received_lots || []).map(x => ({
          product_id: x.product_id,
          product_code: x.product_code,
          product_name: x.product_name,
          lot_no: x.lot_no,
          expired_date: x.expired_date,
          qty_received: Number(x.qty_received) || 0,
          qty_delivered: Number(x.qty_delivered) || 0,
          qty_available: Number(x.qty_available) || 0,
        }));
        // #504: พรีวิวปุ่ม "สร้างออเดอร์เข้าห้องแพ็ค" ต่อใบส่งของ ยิงหลังรู้รายการ DO แล้ว
        // ไม่ await เพื่อไม่ให้หน้ารายละเอียดค้างรอ N คำขอ และความล้มเหลวของพรีวิว
        // ต้องไม่ทำให้หน้าใบสั่งขายพัง (ปุ่มจะไม่โผล่ ซึ่งปลอดภัยกว่าโผล่แล้วโกหก)
        this.loadBoOrderPreviews();
        this.loadGoodsProgress();
      } catch (err) {
        // ไม่ใช้ showErr() ที่นี่ — showErr อาจสั่ง loadDetail() ซ้ำ (กัน recursion)
        swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.loading = false;
      }
    },
    async downloadPoFile() {
      this.downloadingPo = true;
      try {
        const res = await ModernTradeService.saleOrderPoFile(this.$route.params.id);
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        if (d.url) window.open(d.url, "_blank");
      } catch (err) {
        this.showErr(err);
      } finally {
        this.downloadingPo = false;
      }
    },
    async handleConfirm() {
      const result = await swal.fire({
        title: this.$t("modern_trade.sale_order.confirm_confirm"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("confirm") || "Confirm",
        cancelButtonText: this.$t("cancel") || "Cancel",
      });
      if (!result.value) return;
      this.confirming = true;
      try {
        await ModernTradeService.saleOrderConfirm(this.$route.params.id);
        swal.fire({ title: this.$t("modern_trade.sale_order.confirmed"), icon: "success", timer: 1500, showConfirmButton: false });
        this.loadDetail();
      } catch (err) {
        // missing_po_number / missing_po_file / no_items — same text as the PO editor
        const key = saleOrderErrorKey(err && err.response && err.response.data && err.response.data.Msg);
        if (key) swal.fire({ title: this.$t(key), icon: "warning" });
        else this.showErr(err);
      } finally {
        this.confirming = false;
      }
    },
    async handleCancel() {
      const result = await swal.fire({
        title: this.$t("modern_trade.sale_order.cancel_confirm"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("confirm") || "Confirm",
        cancelButtonText: this.$t("cancel") || "Cancel",
      });
      if (!result.value) return;
      this.cancelling = true;
      try {
        await ModernTradeService.saleOrderCancel(this.$route.params.id);
        swal.fire({ title: this.$t("modern_trade.sale_order.cancelled"), icon: "success", timer: 1500, showConfirmButton: false });
        this.loadDetail();
      } catch (err) {
        this.showErr(err);
      } finally {
        this.cancelling = false;
      }
    },
    // #154: ปิดใบ — ส่งของแล้ว (5) → ปิดใบ (6). BE: POST /sale-orders/:id/close (mdf-bo-api#303)
    // 2026-11-04: ยังส่งของไม่ครบ = ปิดได้ แต่ต้องยืนยันเห็นรายการค้างส่งก่อน แล้วส่ง
    // confirm_outstanding: true. BE คำนวณเองอีกรอบ (409 outstanding_items) — ถ้าหน้าจอ
    // ข้อมูลเก่าแล้วเจอ 409 ก็โชว์รายการจาก BE แล้วถามอีกครั้ง
    async handleClose() {
      const owed = outstandingRows(this.items, this.deliveries, this.deliveryItems);
      const ok = owed.length ? await this.confirmCloseOutstanding(owed) : await this.confirmClosePlain();
      if (!ok) return;
      await this.submitClose(owed.length > 0);
    },
    async confirmClosePlain() {
      const result = await swal.fire({
        title: this.$t("modern_trade.sale_order.close_confirm"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("confirm") || "Confirm",
        cancelButtonText: this.$t("cancel") || "Cancel",
      });
      return !!result.value;
    },
    // rows: [{ product_code, product_name, ordered, delivered, remaining|outstanding }]
    async confirmCloseOutstanding(rows) {
      const esc = v =>
        String(v == null ? "" : v).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
      const lines = rows
        .map(r => {
          const left = r.outstanding != null ? r.outstanding : r.remaining;
          const label = [r.product_code, r.product_name].filter(Boolean).join(" ");
          return `<tr><td class="text-left">${esc(label)}</td><td class="text-right">${esc(r.ordered)}</td>` +
            `<td class="text-right">${esc(r.delivered)}</td><td class="text-right text-danger"><strong>${esc(left)}</strong></td></tr>`;
        })
        .join("");
      const html =
        `<p>${esc(this.$t("modern_trade.sale_order.close_outstanding_hint"))}</p>` +
        `<table class="table table-sm"><thead><tr>` +
        `<th class="text-left">${esc(this.$t("modern_trade.sale_order.close_outstanding_col_product"))}</th>` +
        `<th class="text-right">${esc(this.$t("modern_trade.sale_order.close_outstanding_col_ordered"))}</th>` +
        `<th class="text-right">${esc(this.$t("modern_trade.sale_order.close_outstanding_col_delivered"))}</th>` +
        `<th class="text-right">${esc(this.$t("modern_trade.sale_order.close_outstanding_col_outstanding"))}</th>` +
        `</tr></thead><tbody>${lines}</tbody></table>` +
        `<p class="small text-muted">${esc(this.$t("modern_trade.sale_order.close_confirm"))}</p>`;
      const result = await swal.fire({
        title: this.$t("modern_trade.sale_order.close_outstanding_title"),
        html,
        icon: "warning",
        width: 640,
        showCancelButton: true,
        confirmButtonText: this.$t("modern_trade.sale_order.close_outstanding_confirm"),
        cancelButtonText: this.$t("cancel") || "Cancel",
      });
      return !!result.value;
    },
    async submitClose(confirmOutstanding) {
      this.closing = true;
      try {
        const payload = confirmOutstanding ? { confirm_outstanding: true } : {};
        await ModernTradeService.saleOrderClose(this.$route.params.id, payload);
        swal.fire({ title: this.$t("modern_trade.sale_order.closed"), icon: "success", timer: 1500, showConfirmButton: false });
        this.loadDetail();
      } catch (err) {
        const data = err && err.response && err.response.data;
        if (!confirmOutstanding && data && data.Msg === "outstanding_items") {
          // หน้าจอเห็นว่าครบ แต่ BE บอกว่ายังค้าง (มีใบส่งของถูกยกเลิกจากที่อื่น ฯลฯ)
          const rows = (data.Data && data.Data[0] && data.Data[0].outstanding) || [];
          this.closing = false;
          this.loadDetail();
          if (rows.length && (await this.confirmCloseOutstanding(rows))) await this.submitClose(true);
          return;
        }
        this.showErr(err);
      } finally {
        this.closing = false;
      }
    },
    canCancelDeliveryRow(row) {
      return canCancelDelivery({
        canMutate: this.canMutate,
        soStatus: this.so && this.so.status,
        delivery: row,
        hasLiveOrder: !!this.doLiveOrder(row),
      });
    },
    // 2026-11-04: ยกเลิกใบส่งของ — POST /deliveries/:id/cancel. ใบส่งของไม่ได้ตัดสต็อก
    // ตอนออก จึงไม่มีสต็อกให้คืน; BE ปฏิเสธ (409) ถ้ามีใบกำกับ/ออเดอร์ห้องแพ็ค/ขายฝากถูกขายแล้ว
    async handleCancelDelivery(row) {
      if (!this.canCancelDeliveryRow(row) || this.cancellingDeliveryId !== null) return;
      const result = await swal.fire({
        title: this.$t("modern_trade.delivery.cancel.confirm_title", { doc_no: row.doc_no || "#" + row.id }),
        text: this.$t("modern_trade.delivery.cancel.confirm_hint"),
        icon: "warning",
        input: "text",
        inputPlaceholder: this.$t("modern_trade.delivery.cancel.reason_placeholder"),
        showCancelButton: true,
        confirmButtonText: this.$t("modern_trade.delivery.cancel.action"),
        cancelButtonText: this.$t("cancel") || "Cancel",
      });
      // sweetalert2 v9: confirm with an empty input → value "" (still a confirm)
      if (result.dismiss || result.value === undefined) return;
      this.cancellingDeliveryId = row.id;
      try {
        await ModernTradeService.deliveryCancel(row.id, { reason: result.value || "" });
        swal.fire({ title: this.$t("modern_trade.delivery.cancel.done"), icon: "success", timer: 1500, showConfirmButton: false });
        this.loadDetail();
      } catch (err) {
        swal.fire({ title: this.$t("modern_trade.delivery.cancel.failed"), text: this.errMsg(err), icon: "error" });
        if (err && err.response && err.response.status === 409) this.loadDetail();
      } finally {
        this.cancellingDeliveryId = null;
      }
    },
    async handleGenProductionOrders() {
      this.genningMo = true;
      try {
        await ModernTradeService.saleOrderProductionOrders(this.$route.params.id);
        this.loadDetail();
      } catch (err) {
        this.showErr(err);
      } finally {
        this.genningMo = false;
      }
    },
    // #183: navigate to the print-set preview page (lets the user pick which docs to print).
    // ปุ่มนี้อยู่ในตารางใบสั่งผลิต ⇒ ขอให้ preview ติ๊ก "ใบสั่งผลิต" มาให้เลย
    downloadMoPdf() {
      this.goPrintPreview("production");
    },
    // Nov-5 §4: one Lot/Expire template per SO (rows with remaining > 0).
    async downloadSoLotTemplate() {
      await this.downloadBlob(
        () => ModernTradeService.saleOrderLotTemplate(this.$route.params.id),
        `${(this.so && this.so.so_no) || "SO"}_lot_template.xlsx`,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );
    },
    // #183: navigate to the print-set preview page (lets the user pick which docs to print).
    // ปุ่มนี้อยู่ในตารางใบส่งของ ⇒ ขอให้ preview ติ๊ก "ใบส่งของ" มาให้เลย
    downloadDoPdf() {
      this.goPrintPreview("delivery");
    },
    // Resolve a DO's tracking number: its own (operator-entered) value wins.
    // Legacy fallback: sale orders created back when MT still opened a BO order
    // carry their tracking on that order — new SOs have no bo_order at all.
    doTracking(row) {
      if (row && row.tracking_number) return row.tracking_number;
      if (this.hasBoOrder) {
        return this.boOrder.tracking_number || this.boOrder.shipping_tracking_number || "";
      }
      return "";
    },
    // ── #504 สร้างออเดอร์เข้าห้องแพ็ค ─────────────────────────────────────
    // ทุก predicate ด้านล่างอ่านจากพรีวิวของ BE ล้วน ๆ ไม่มีการเดาจาก so/row เอง:
    // เงื่อนไข (ผูก WMS, LOT/วันหมดอายุ, ตั้งค่าโมดูล, ตัดสต็อกไปแล้ว) รู้ได้ที่ BE
    // เท่านั้น และปุ่มที่หน้าจอเปิดเองโดยที่ BE จะปฏิเสธ = ผู้ใช้กดแล้วเจอ error เปล่า ๆ
    // ยิงทีละใบ ไม่ใช่ Promise.all — DO หนึ่งใบ = หนึ่ง request และ SO ที่มีใบส่งของ
    // หลายใบจะกลายเป็น request storm พร้อมกันทันทีที่เปิดหน้า (แต่ละคำขอฝั่ง BE
    // เปิด transaction + อ่าน bo_mt_product/wms_product ทีละบรรทัด)
    //
    // ทำ lazy ตอนกดเปิด modal ไม่ได้ เพราะปุ่มต้องรู้ตั้งแต่เรนเดอร์ว่ากดได้ไหม
    // และต้องโชว์เหตุผลกับเลขออเดอร์ที่มีอยู่แล้ว (issue #236 ข้อ 1) — ถ้ารอจนกด
    // ผู้ใช้จะเห็นปุ่มเขียวทุกใบแล้วค่อยโดนปฏิเสธทีหลัง ซึ่งแย่กว่าเดิม
    //
    // ตามลำดับแถวและหยุดทันทีถ้าออกจากหน้าไปแล้ว ⇒ จำนวนคำขอเท่าเดิมแต่ไม่กระแทก
    // พร้อมกัน (SO หนึ่งใบมีใบส่งของได้หลายใบ — โรงงานส่งของเป็นงวด, owner
    // 2026-11-04 — ปุ่มออกใบส่งของโผล่ตราบที่ยังมีของเหลือให้ส่ง ดู showCreateDelivery)
    async loadBoOrderPreviews() {
      const rows = this.deliveries || [];
      if (!rows.length) {
        this.boOrderPreviews = {};
        return;
      }
      const token = ++this.boOrderPreviewToken;
      const map = {};
      for (const d of rows) {
        // ใบส่งของที่ยกเลิกแล้วสร้างออเดอร์ไม่ได้อีก (BE ตอบ delivery_cancelled) — ไม่ต้องถาม
        if (d.status === 9) {
          map[d.id] = null;
          continue;
        }
        try {
          map[d.id] = await this.fetchBoOrderPreview(d.id);
        } catch (err) {
          // เงียบโดยตั้งใจ: พรีวิวเป็นข้อมูลเสริมของการ์ด ถ้าเรียกไม่ได้ให้ไม่มีปุ่ม
          map[d.id] = null;
        }
        // มี loadDetail() รอบใหม่แซงมาแล้ว (หรือ user เปลี่ยนหน้า) — ทิ้งผลของรอบนี้
        // ไม่งั้นผลเก่าจะไปเขียนทับพรีวิวที่ใหม่กว่า
        if (token !== this.boOrderPreviewToken) return;
      }
      this.boOrderPreviews = map;
    },
    async fetchBoOrderPreview(deliveryId) {
      const res = await ModernTradeService.deliveryBoOrderPreview(deliveryId);
      const body = (res && res.data) || {};
      return (body.Data && body.Data[0]) || body.Datum || null;
    },
    doPreview(row) {
      return (row && this.boOrderPreviews[row.id]) || null;
    },
    // ออเดอร์ที่ "ยังมีชีวิต" = มีอยู่จริงและยังไม่ถูกยกเลิก (140)
    // ยกเลิกแล้วต้องกดสร้างใหม่ได้ (spec §3 case F) จึงไม่นับเป็นออเดอร์ของใบนี้อีก
    doLiveOrder(row) {
      const p = this.doPreview(row);
      const o = p && p.existing_bo_order;
      if (!o || !o.id) return null;
      return Number(o.order_status) === 140 ? null : o;
    },
    // not_applicable / legacy_bo_order = ไม่แสดงปุ่มเลย (ไม่ใช่ปุ่มเทา)
    // ใบพวกนี้ไม่มีทางสร้างออเดอร์ได้ ปุ่มเทาค้างไว้มีแต่ทำให้คนพยายามกด
    doShowCreateButton(row) {
      if (row && row.status === 9) return false;
      const p = this.doPreview(row);
      if (!p) return false;
      if (p.reason_code === "not_applicable" || p.reason_code === "legacy_bo_order") return false;
      return !this.doLiveOrder(row);
    },
    doCanCreate(row) {
      const p = this.doPreview(row);
      return !!(p && p.can_create);
    },
    doReasonText(row) {
      return this.doReasonTextOf(this.doPreview(row));
    },
    // ข้อความของ BE มาก่อนเสมอ: กรณี missing_wms_mapping / missing_lot_expire
    // มันคือที่เดียวที่ลิสต์รหัสหรือบรรทัดที่ผิดครบทุกตัว คำแปลของ FE เป็นแค่ตัวสำรอง
    // เผื่อ BE ส่ง reason_message ว่างมา
    doReasonTextOf(preview) {
      if (!preview) return "";
      if (preview.reason_message) return preview.reason_message;
      const code = preview.reason_code || "unknown";
      const key = `modern_trade.delivery.bo_order.reason.${code}`;
      const label = this.$t(key);
      return label === key ? this.$t("modern_trade.delivery.bo_order.reason.unknown") : label;
    },
    // go-core ใส่ payload ของ error ไว้ที่ Data[] (r.Append) — Datum เป็นทางของ r.Set
    // อ่านทั้งสองทางเพราะ handler คนละตัวใช้คนละแบบ และเผื่อ body แบนมาตรง ๆ
    extractRejection(err) {
      const body = (err && err.response && err.response.data) || {};
      const payload = (body.Data && body.Data[0]) || body.Datum || body;
      if (!payload || typeof payload !== "object") return null;
      return {
        reason_code: payload.reason_code || body.Msg || "",
        missing_lots: Array.isArray(payload.missing_lots) ? payload.missing_lots : [],
      };
    },
    // missing_lots ของ BE: [{ line (ฐาน 1), lot_no, missing_lot, missing_expired_date }]
    // nil-normalize เป็น [] มาแล้วฝั่ง BE แต่ฝั่งนี้ยังต้องกันของที่ไม่ใช่ array
    // (BE รุ่นเก่าไม่มี field นี้เลย) แล้วเรียงตามเลขบรรทัดที่ BE ส่งมา — เลขบรรทัด
    // เป็นของ BE ทั้งหมด หน้าจอไม่คำนวณตำแหน่งเองสักตัว
    normalizeMissingLots(list) {
      if (!Array.isArray(list)) return [];
      return list
        .filter(x => x && (x.missing_lot || x.missing_expired_date || Number(x.line) > 0))
        .map(x => ({
          line: Number(x.line) || 0,
          lot_no: x.lot_no || "",
          missing_lot: !!x.missing_lot,
          missing_expired_date: !!x.missing_expired_date,
        }))
        .sort((a, b) => a.line - b.line);
    },
    // #240 nit — รหัส/ชื่อสินค้าของบรรทัดใน missing_lots
    // คืน "" เมื่อไม่มั่นใจ (BE รุ่นที่ยังไม่ส่ง delivery_items, ตำแหน่งเลื่อน) —
    // ตารางจะโชว์ "-" ช่องว่างดีกว่าเดาชื่อสินค้าผิดตัว
    //
    // ต้องเทียบ LOT ก่อนแสดงเสมอ: deliveryItems โหลดครั้งเดียวตอน saleOrderGet แต่
    // missing_lots มาจากพรีวิวที่ดึงสดทุกครั้งที่เปิด modal ⇒ ถ้ามีคนเพิ่ม/ลบบรรทัด
    // ในใบส่งของจากอีกแท็บระหว่างที่หน้านี้เปิดค้าง ตำแหน่งของสองฝั่งจะเลื่อนออกจากกัน
    // และคอลัมน์นี้จะชี้ผิดตัวเงียบ ๆ (หน้านี้ไม่มีเส้น API ที่ดึงบรรทัดของใบส่งของ
    // ใบเดียวมาใหม่ได้ — modernTrade.js มีแค่ PDF/ship-status/bo-order)
    //
    // BE trim LotNo มาแล้ว (MT-BoOrderFromDelivery.go:710-712) ฝั่งนี้เป็น *string
    // จึงเป็น null ได้ ⇒ normalize ให้เหมือนกันก่อนเทียบ
    // ข้อจำกัดที่รู้ตัว: แถวที่ "ไม่มี LOT เลย" ทั้งคู่เป็น "" เท่ากัน การเทียบจึงแยก
    // ไม่ออกถ้าตำแหน่งเลื่อนไปตรงกับอีกแถวที่ก็ไม่มี LOT — แต่แถวแบบนั้น BE ก็ flag
    // ว่า missing_lot อยู่แล้ว ชื่อที่หลุดออกมาจึงยังเป็นสินค้าที่อยู่ในตารางนี้เอง
    missingLotProduct(row) {
      if (!row) return "";
      const idx = Number(row.line) - 1;
      const item = idx >= 0 ? this.boOrderDeliveryLines[idx] : null;
      if (!item) return "";
      const lot = String(item.lot_no === null || item.lot_no === undefined ? "" : item.lot_no).trim();
      if (lot !== String(row.lot_no || "")) return "";
      const code = item.product_code || "";
      const named = this.items.find(x => x && Number(x.product_id) === Number(item.product_id));
      const name = (named && named.product_name) || "";
      if (code && name) return `${code} — ${name}`;
      return code || name;
    },
    // ไม่มี LOT เลยรุนแรงกว่า (แดง) เพราะบรรทัดนั้นยังไม่รู้ว่าจะส่งของล็อตไหนออกไป
    missingLotRowClass({ row }) {
      return row && row.missing_lot ? "mt-missing-lot-row" : "mt-missing-expire-row";
    },
    boOrderStatusText(order) {
      if (!order) return "";
      if (order.status_text) return order.status_text;
      if (Number(order.order_status) === 111) return this.$t("modern_trade.delivery.bo_order.status_111");
      return `#${order.order_status}`;
    },
    providerName(id) {
      const p = this.shippingProviders.find(x => Number(x.id) === Number(id));
      return p ? p.name : id ? `#${id}` : "-";
    },
    async openBoOrderDialog(row) {
      // ของที่ค้างจากการกดยืนยันครั้งก่อนต้องหายไปตอนเปิดใหม่ ไม่งั้นบรรทัดที่แก้ไปแล้ว
      // จะยังโชว์อยู่ทั้งที่ BE ไม่ได้ว่าอะไรแล้ว
      this.boOrderRejection = null;
      this.boOrderForm = { delivery: row, preview: this.doPreview(row), provider_id: null };
      this.showBoOrderModal = true;
      this.boOrderLoading = true;
      // ยิงพรีวิวใหม่ตอนเปิด modal เสมอ ไม่ใช้ของที่แคชไว้ตอนโหลดหน้า:
      // จำนวนในตารางคือจำนวนที่จะถูกกันจริง ถ้าหน้าเปิดค้างไว้นานแล้วมีคนแก้ใบส่งของ
      // หรือสร้างออเดอร์จากอีกแท็บ พรีวิวเก่าจะกลายเป็นคำโกหกที่ผู้ใช้กดยืนยัน
      try {
        const fresh = await this.fetchBoOrderPreview(row.id);
        this.boOrderForm.preview = fresh;
        this.boOrderPreviews = Object.assign({}, this.boOrderPreviews, { [row.id]: fresh });
        const shipping = (fresh && fresh.shipping) || {};
        this.boOrderForm.provider_id = shipping.provider_id || null;
      } catch (err) {
        this.showBoOrderModal = false;
        this.showErr(err);
      } finally {
        this.boOrderLoading = false;
      }
    },
    resetBoOrderForm() {
      this.boOrderForm = { delivery: null, preview: null, provider_id: null };
      this.boOrderRejection = null;
    },
    async submitBoOrder() {
      const row = this.boOrderForm.delivery;
      if (!row || !this.boOrderCanSubmit) return;
      this.boOrderSubmitting = true;
      try {
        const payload = {};
        if (!this.boOrderProviderLocked) payload.shipping_provider_id = this.boOrderForm.provider_id;
        const res = await ModernTradeService.deliveryBoOrderCreate(row.id, payload);
        const body = (res && res.data) || {};
        const out = (body.Data && body.Data[0]) || body.Datum || {};
        this.showBoOrderModal = false;
        swal.fire({
          title: this.$t("modern_trade.delivery.bo_order.created"),
          // เลขพัสดุว่าง = ขนส่งออกเลขแบบ async ต้องบอกว่า "กำลังขอ" ไม่ใช่ช่องว่าง
          text: `${this.$t("modern_trade.delivery.bo_order.created_detail", {
            id: out.bo_order_id || "-",
            status: this.boOrderStatusText({ order_status: out.order_status }),
          })} · ${out.tracking_number || this.$t("modern_trade.delivery.bo_order.tracking_pending")}`,
          icon: "success",
        });
        this.loadDetail();
      } catch (err) {
        // 409/400 มาพร้อม reason_code + reason_message — errMsg() คืน Cause (ข้อความไทย
        // ที่ลิสต์รหัส/บรรทัดที่ผิดครบ) แล้วรีเฟรชพรีวิวให้ปุ่มตรงกับความจริงทันที
        // ทั้งในตาราง และใน modal ที่ยังเปิดค้างอยู่ตรงหน้าผู้ใช้ — ไม่งั้นเขาจะกดยืนยัน
        // ซ้ำบนพรีวิวที่ BE เพิ่งบอกไปแล้วว่าใช้ไม่ได้
        swal.fire({ title: this.errMsg(err), icon: "error" });
        // #240 — body ของ 409/400 พก missing_lots มาด้วย (Data[0]) เก็บไว้ก่อนดึง
        // พรีวิวใหม่ เพราะถ้าพรีวิวรอบใหม่ไม่มี field นี้ (หรือดึงไม่สำเร็จ) นี่คือ
        // ข้อมูลเดียวที่บอกได้ว่าบรรทัดไหนทำให้เพิ่งถูกปฏิเสธ
        this.boOrderRejection = this.extractRejection(err);
        this.loadBoOrderPreviews();
        try {
          this.boOrderForm.preview = await this.fetchBoOrderPreview(row.id);
        } catch (e2) {
          this.showBoOrderModal = false;
        }
      } finally {
        this.boOrderSubmitting = false;
      }
    },
    openShipStatusDialog(row) {
      this.shipForm = {
        row,
        // Default to the row's CURRENT status, never the opposite one. This dialog
        // is also the only way to key a tracking number in, so someone opening it
        // to fix a typo on an already-shipped DO must not have it silently armed to
        // revert the row to "รอส่งของ" — that write clears shipped_at/shipped_by.
        // Flipping the status is an explicit choice in the select below.
        ship_status: row.ship_status === 2 ? 2 : 1,
        // Pre-fill with what the DO row actually displays. submitShipStatus()
        // always sends this field, so leaving it blank would wipe an existing
        // number just because the user came here to change the ship status.
        tracking_number: this.doTracking(row),
      };
      this.showShipModal = true;
    },
    async submitShipStatus() {
      if (!this.shipForm.row) return;
      this.shipSaving = true;
      try {
        // Tracking is operator-entered for every shipping type now. Always sent,
        // so an empty box deliberately clears it — openShipStatusDialog() therefore
        // pre-fills the box with the number currently shown for this DO.
        const payload = {
          ship_status: this.shipForm.ship_status,
          tracking_number: (this.shipForm.tracking_number || "").trim(),
        };
        await ModernTradeService.deliveryShipStatus(this.shipForm.row.id, payload);
        this.showShipModal = false;
        swal.fire({
          title: this.$t("modern_trade.delivery.ship_status.saved"),
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        this.loadDetail();
      } catch (err) {
        this.showErr(err);
      } finally {
        this.shipSaving = false;
      }
    },
    async downloadBlob(fn, filename, mime) {
      try {
        const res = await fn();
        const blob = new Blob([res.data], { type: mime });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        this.showErr(err);
      }
    },
    // ── Lot form (Nov-5 §4) ──────────────────────────────────────────────
    async loadGoodsProgress() {
      if (!this.showGoodsCard) {
        this.goodsProgress = null;
        return;
      }
      try {
        const res = await ModernTradeService.saleOrderGoodsProgress(this.$route.params.id);
        this.goodsProgress = progressRows(res.data);
      } catch (err) {
        // Progress is advisory: on failure keep the old status-only gate.
        this.goodsProgress = null;
      }
    },
    lotProductLabel(p) {
      // progress rows carry the SKU as product_code (code may be empty)
      return mtProductLabel(Object.assign({}, p, { code: (p && (p.code || p.product_code)) || "" }));
    },
    lotRowsOf(productId) {
      const out = [];
      this.lotRows.forEach((row, idx) => {
        if (row.product_id === productId) out.push({ row, idx });
      });
      return out;
    },
    lotEnteredQty(productId) {
      return this.lotRows.reduce((sum, r) => {
        const n = Number(r.qty);
        return r.product_id === productId && Number.isInteger(n) && n > 0 ? sum + n : sum;
      }, 0);
    },
    addLotRow(productId) {
      this.lotRowSeq += 1;
      // Insert after this product's last row so the flat order (= row_no) follows the screen.
      let at = this.lotRows.length;
      for (let i = this.lotRows.length - 1; i >= 0; i--) {
        if (this.lotRows[i].product_id === productId) {
          at = i + 1;
          break;
        }
      }
      this.lotRows.splice(at, 0, { uid: this.lotRowSeq, product_id: productId, lot_no: "", expired_date: "", qty: "" });
    },
    removeLotRow(idx) {
      this.lotRows.splice(idx, 1);
    },
    lotRowError(idx) {
      return this.lotCheck.rowErrors[idx] || this.lotServerErrors[idx] || null;
    },
    // BE row code → text (manual form + Excel wizard error_msg); unknown → raw
    lotErrorText(code) {
      const key = lotErrorKey(code);
      return key ? this.$t(key) : code;
    },
    refreshLotToday() {
      this.lotToday = localToday();
    },
    async saveLots() {
      this.refreshLotToday();
      if (!this.lotCheck.ok || this.savingLots) return;
      this.savingLots = true;
      this.lotServerErrors = {};
      try {
        const res = await ModernTradeService.saleOrderGoodsManual(this.$route.params.id, manualPayload(this.lotRows));
        const body = (res && res.data) || {};
        const d = body.Datum || (body.Data && body.Data[0]) || body;
        this.lotRows = [];
        this.loadDetail();
        swal.fire({
          title: this.$t(d && d.fully_received ? "modern_trade.lot_form.saved_full" : "modern_trade.lot_form.saved"),
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (err) {
        const rowErrs = rowErrorsFromServer(err && err.response && err.response.data, this.lotRows.length);
        if (rowErrs && Object.keys(rowErrs).length) {
          this.lotServerErrors = rowErrs;
          swal.fire({ title: this.$t("modern_trade.lot_form.server_rejected"), icon: "error" });
        } else {
          this.showErr(err);
        }
      } finally {
        this.savingLots = false;
      }
    },
    // ── Goods import wizard ──────────────────────────────────────────────
    onGoodsFileChange(e) {
      this.goodsFile = e.target.files[0] || null;
    },
    async doValidate() {
      if (!this.goodsFile) return;
      this.validating = true;
      try {
        const fd = new FormData();
        fd.append("file", this.goodsFile);
        const res = await ModernTradeService.saleOrderGoodsValidate(this.$route.params.id, fd);
        const d = (res.data && res.data.Datum) || {};
        this.batch = {
          id: d.batch_id,
          row_total: d.row_total || 0,
          row_ok: d.row_ok || 0,
          row_error: d.row_error || 0,
        };
        this.previewRows = d.rows || [];
        this.wizardStep = 2;
      } catch (err) {
        this.showErr(err);
      } finally {
        this.validating = false;
      }
    },
    async doConfirmGoods() {
      this.confirmingGoods = true;
      try {
        await ModernTradeService.saleOrderGoodsConfirm(this.$route.params.id, { batch_id: this.batch.id });
        this.batch.id = null;
        this.resetWizard();
        this.loadDetail();
        swal.fire({ title: this.$t("modern_trade.import.import_success"), icon: "success", timer: 1500, showConfirmButton: false });
      } catch (err) {
        this.showErr(err);
      } finally {
        this.confirmingGoods = false;
      }
    },
    resetWizard() {
      this.cancelPendingBatch();
      this.wizardStep = 1;
      this.goodsFile = null;
      this.batch = { id: null, row_total: 0, row_ok: 0, row_error: 0 };
      this.previewRows = [];
      if (this.$refs.goodsFileInput) this.$refs.goodsFileInput.value = "";
    },
    cancelPendingBatch() {
      const bid = this.batch && this.batch.id;
      if (!bid) return;
      this.batch.id = null;
      ModernTradeService.batchCancel(bid).catch(() => {});
    },
    // ── Delivery ─────────────────────────────────────────────────────────
    openDeliveryDialog() {
      const today = new Date();
      const pad = n => String(n).padStart(2, "0");
      this.deliveryForm = {
        delivery_date: `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`,
      };
      if (this.deliveryNeedsLot) {
        // qty_available is authoritative from BE (received_lots contract) —
        // already nets out prior deliveries across DOs; do not recompute here.
        // Only lots with something left are offered.
        this.deliveryLotRows = deliverableLots(this.receivedLots).map(l => {
          return {
            product_id: l.product_id,
            product_code: l.product_code,
            product_name: l.product_name,
            lot_no: l.lot_no,
            expired_date: l.expired_date,
            qty_received: l.qty_received,
            qty_delivered: l.qty_delivered,
            available: l.qty_available,
            qty: 0,
          };
        });
      } else {
        // Pre-fill what is still to deliver (SO qty − earlier non-cancelled DOs);
        // the BE caps the same way.
        this.deliveryProductRows = productRemainingRows(this.items, this.deliveries, this.deliveryItems)
          .filter(r => r.remaining > 0)
          .map(r => ({
            product_id: r.product_id,
            product_code: r.product_code,
            product_name: r.product_name,
            delivered: r.delivered,
            remaining: r.remaining,
            qty: r.remaining,
          }));
      }
      this.showDeliveryModal = true;
    },
    resetDeliveryForm() {
      this.deliveryForm = { delivery_date: "" };
      this.deliveryLotRows = [];
      this.deliveryProductRows = [];
    },
    async handleCreateDelivery() {
      if (!this.deliveryForm.delivery_date) {
        swal.fire({ title: this.$t("modern_trade.sale_order.delivery_date"), icon: "warning" });
        return;
      }
      let itemsPayload;
      if (this.deliveryNeedsLot) {
        itemsPayload = this.deliveryLotRows
          .filter(r => Number(r.qty) > 0)
          .map(r => ({ product_id: r.product_id, lot_no: r.lot_no, qty: Number(r.qty) }));
      } else {
        itemsPayload = this.deliveryProductRows
          .filter(r => Number(r.qty) > 0)
          .map(r => ({ product_id: r.product_id, qty: Number(r.qty) }));
      }
      if (!itemsPayload.length) {
        swal.fire({ title: this.$t("modern_trade.sale_order.items_required"), icon: "warning" });
        return;
      }
      this.creatingDelivery = true;
      try {
        // No shipping_provider_id: the endpoint still accepts the field but ignores
        // it (it only fed the BO order MT no longer creates), so sending it would
        // just make operators pick a carrier that goes nowhere.
        const payload = { delivery_date: this.deliveryForm.delivery_date, items: itemsPayload };
        await ModernTradeService.saleOrderDeliveries(this.$route.params.id, payload);
        this.showDeliveryModal = false;
        swal.fire({ title: this.$t("modern_trade.sale_order.saved"), icon: "success", timer: 1500, showConfirmButton: false });
        this.loadDetail();
      } catch (err) {
        this.showErr(err);
      } finally {
        this.creatingDelivery = false;
      }
    },
    // ── Payment (auto-creates the tax invoice from the delivered amount) ──
    openPaymentDialog() {
      // ยอดตั้งต้น = ยอดคงค้าง (computed `outstanding`) ซึ่งเป็นเพดานด้วย
      this.paymentForm = {
        amount: this.outstanding || null,
        paid_date: new Date().toISOString().slice(0, 10),
        method: "โอนเงิน",
        note: "",
        slip_object_key: "",
        slip_cdn_url: "",
      };
      this.showPaymentModal = true;
    },
    clearSlip() {
      this.paymentForm.slip_object_key = "";
      this.paymentForm.slip_cdn_url = "";
      if (this.$refs.slipInput) this.$refs.slipInput.value = "";
    },
    // Build a CDN URL from a stored slip object key (mdf-front is private; only
    // cdn.madamefin.com serves it).
    slipUrl(key) {
      if (!key) return "";
      if (/^https?:\/\//i.test(key)) return key;
      return "https://cdn.madamefin.com/" + String(key).replace(/^\/+/, "");
    },
    async onSlipPicked(ev) {
      const file = ev.target.files && ev.target.files[0];
      if (!file) return;
      const okType = ["image/jpeg", "image/png", "image/webp", "application/pdf"].indexOf(file.type) !== -1;
      if (!okType) {
        swal.fire({ title: this.$t("modern_trade.payment.slip_bad_type"), icon: "warning" });
        this.clearSlip();
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        swal.fire({ title: this.$t("modern_trade.payment.slip_too_big"), icon: "warning" });
        this.clearSlip();
        return;
      }
      this.slipUploading = true;
      try {
        const presignRes = await ModernTradeService.slipPresign({
          file_name: file.name,
          content_type: file.type,
        });
        const d = (presignRes.data && presignRes.data.Data && presignRes.data.Data[0]) || {};
        if (!d.upload_url || !d.object_key) throw new Error("presign failed");
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", d.upload_url);
          xhr.setRequestHeader("Content-Type", file.type);
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error("Upload failed: " + xhr.status));
          };
          xhr.onerror = () => reject(new Error("Network error"));
          xhr.send(file);
        });
        this.paymentForm.slip_object_key = d.object_key;
        this.paymentForm.slip_cdn_url = d.cdn_url || this.slipUrl(d.object_key);
      } catch (err) {
        const detail = err && err.message ? ": " + err.message : "";
        swal.fire({ title: this.$t("modern_trade.payment.slip_upload_failed") + detail, icon: "error" });
        this.clearSlip();
      } finally {
        this.slipUploading = false;
      }
    },
    // round-19: ออกใบกำกับภาษีจากยอดรับเงินที่ยังไม่ได้ออกใบ (un-invoiced pool)
    async handleIssueInvoice() {
      const amount = this.paymentCap ? this.money(this.paymentCap.uninvoiced_amount) : "";
      const result = await swal.fire({
        title: this.$t("modern_trade.invoice.issue_confirm", { amount }),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$t("confirm") || "Confirm",
        cancelButtonText: this.$t("cancel") || "Cancel",
      });
      if (!result.value) return;
      this.issuingInvoice = true;
      try {
        await ModernTradeService.saleOrderIssueInvoice(this.$route.params.id);
        swal.fire({ title: this.$t("modern_trade.invoice.issued"), icon: "success", timer: 1500, showConfirmButton: false });
        this.loadDetail();
      } catch (err) {
        this.showInvoiceErr(err);
      } finally {
        this.issuingInvoice = false;
      }
    },
    // ── §3A ออกใบกำกับภาษีจากใบส่งของ (1 ใบต่อ 1 ใบส่งของ) ─────────────────
    canIssueFromDeliveryRow(row) {
      return canIssueFromDelivery(row, {
        canMutate: this.canMutate,
        saleType: this.so && this.so.sale_type,
        paymentCap: this.paymentCap,
      });
    },
    deliveryInvoiceDocNo(row) {
      const inv = (this.invoices || []).find(i => Number(i.id) === Number(row.invoice_id));
      return (inv && inv.doc_no) || "#" + row.invoice_id;
    },
    async handleIssueFromDelivery(row) {
      if (!this.canIssueFromDeliveryRow(row) || this.issuingDeliveryId !== null) return;
      const result = await swal.fire({
        title: this.$t("modern_trade.invoice.from_delivery_confirm", { doc_no: row.doc_no || "#" + row.id }),
        text: this.$t("modern_trade.invoice.from_delivery_confirm_hint"),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$t("confirm") || "Confirm",
        cancelButtonText: this.$t("cancel") || "Cancel",
      });
      // sweetalert2 v9 → result.value
      if (!result.value) return;
      this.issuingDeliveryId = row.id;
      try {
        const res = await ModernTradeService.saleOrderInvoiceFromDelivery(this.$route.params.id, row.id);
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        swal.fire({
          title: this.$t("modern_trade.invoice.issued"),
          text: this.$t("modern_trade.invoice.from_delivery_issued", {
            doc_no: d.doc_no || (d.invoice_id ? "#" + d.invoice_id : "-"),
            net: this.money(d.net_amount),
            linked: this.money(d.linked_amount),
          }),
          icon: "success",
        });
        this.loadDetail();
      } catch (err) {
        this.showInvoiceErr(err);
      } finally {
        this.issuingDeliveryId = null;
      }
    },
    // รหัส 409/400 ของเส้นออกใบ → ข้อความของหน้าจอ แล้วโหลดใหม่ (สถานะเปลี่ยนไปแล้ว)
    showInvoiceErr(err) {
      const code = (err && err.response && err.response.data && err.response.data.Msg) || "";
      const key = invoiceErrorKey(code);
      const status = err && err.response && err.response.status;
      if (!key) {
        this.showErr(err);
        // 409 = สถานะบนเซิร์ฟเวอร์เปลี่ยนไปแล้ว แม้ไม่รู้จักรหัส ก็ต้องโหลดใหม่
        if (status === 409) this.loadDetail();
        return;
      }
      swal.fire({ title: this.$t(key), icon: "warning" });
      this.loadDetail();
    },
    async submitPayment() {
      if (!this.paymentForm.amount || this.paymentForm.amount <= 0) {
        swal.fire({ title: this.$t("modern_trade.payment.amount_positive"), icon: "warning" });
        return;
      }
      // #154: กันรับเงินเกินยอดคงค้างตั้งแต่ FE (BE ยังมี guard payment_exceeds กันซ้ำ
      // เผื่อ race — error ของ BE ถูกโชว์ผ่าน errMsg() ด้านล่าง)
      if (this.amountExceedsOutstanding) {
        swal.fire({
          title: this.$t("modern_trade.payment.exceeds_outstanding", { amount: this.money(this.outstanding) }),
          icon: "warning",
        });
        return;
      }
      if (!this.paymentForm.paid_date) {
        swal.fire({ title: this.$t("modern_trade.payment.date_required"), icon: "warning" });
        return;
      }
      this.paymentSaving = true;
      try {
        await ModernTradeService.saleOrderPaymentCreate(this.$route.params.id, {
          amount: Number(this.paymentForm.amount),
          paid_date: this.paymentForm.paid_date,
          method: this.paymentForm.method || null,
          note: this.paymentForm.note || null,
          slip_object_key: this.paymentForm.slip_object_key || null,
        });
        this.showPaymentModal = false;
        swal.fire({ title: this.$t("modern_trade.payment.recorded"), icon: "success", timer: 1500, showConfirmButton: false });
        this.loadDetail();
      } catch (err) {
        // BE guard `payment_exceeds` (MT-Payment.go) — Cause เป็นข้อความไทยอ่านได้อยู่แล้ว
        // #154: sync เพดานจริงจาก BE เมื่อโดนตีกลับ (race — คนอื่นบันทึกรับเงินไปก่อน
        // ระหว่างที่ modal เปิดอยู่) ให้ modal โชว์ยอดคงค้างที่ถูกต้องทันทีโดยไม่ต้องปิด/เปิดใหม่
        //
        // #303: error payload มีแค่ยอด (outstanding/paid/net) ไม่มี invoice_id / can_receive
        // → merge ทับของเดิมแทนการแทนที่ทั้งก้อน ไม่งั้น field อื่นของ payment_cap หายไป
        const datum = err && err.response && err.response.data && err.response.data.Datum;
        if (datum && datum.outstanding != null) {
          this.paymentCap = Object.assign({}, this.paymentCap, datum);
        }
        this.showErr(err);
      } finally {
        this.paymentSaving = false;
      }
    },
    // #183: navigate to the print-set preview page (lets the user pick which docs to print).
    // ปุ่มนี้อยู่ในตารางใบกำกับภาษี ⇒ ขอให้ preview ติ๊ก "ใบกำกับภาษี" มาให้เลย
    downloadInvoicePdf() {
      this.goPrintPreview("invoice");
    },
    goInvoices() {
      this.$router.push({ name: "MtInvoices" });
    },
    // ── Labels / helpers ─────────────────────────────────────────────────
    // go-core envelope: { Status, Msg (error code), Cause (human message) }
    errMsg(err) {
      const d = (err && err.response && err.response.data) || {};
      return d.Cause || d.Msg || (err && err.message) || "Error";
    },
    // จุดเดียวสำหรับโชว์ error ของทุก mutation.
    // #303: BE ตีกลับ 409 `sale_order_closed` เมื่อใบถูกปิดไปแล้ว (เช่น อีกแท็บ/อีกคนกดปิดใบ
    // ระหว่างที่หน้านี้เปิดค้าง) — โชว์ Cause (ไทย) แล้วโหลดใหม่ ให้ปุ่ม mutation หายทันที
    showErr(err) {
      swal.fire({ title: this.errMsg(err), icon: "error" });
      const status = err && err.response && err.response.status;
      const code = (err && err.response && err.response.data && err.response.data.Msg) || "";
      // ฝากขาย: BE ตีกลับ 409 `wrong_sale_type` (หน้าเปิดค้าง/ข้อมูลเก่า) — errMsg() คืน
      // Cause หรือ Msg จาก BE เสมอ (ไม่มีทางว่าง) แล้วโหลดใหม่ให้ปุ่มที่ไม่ควรมีหายไป
      if (status === 409 && (code === "sale_order_closed" || code === "already_closed" || code === "wrong_sale_type")) {
        this.loadDetail();
      }
    },
    saleTypeLabel(t) {
      if (t === 1) return this.$t("modern_trade.sale_order.sale_type_consignment");
      if (t === 2) return this.$t("modern_trade.sale_order.sale_type_outright");
      return "-";
    },
    shippingTypeLabel(t) {
      switch (t) {
        case 1: return this.$t("modern_trade.sale_order.shipping_type_pack");
        case 2: return this.$t("modern_trade.sale_order.shipping_type_factory");
        case 3: return this.$t("modern_trade.sale_order.shipping_type_express");
        default: return "-";
      }
    },
    statusLabel(s) {
      switch (s) {
        case 1: return this.$t("modern_trade.sale_order.status_draft");
        case 2: return this.$t("modern_trade.sale_order.status_confirmed");
        case 3: return this.$t("modern_trade.sale_order.status_in_production");
        case 4: return this.$t("modern_trade.sale_order.status_goods_received");
        case 5: return this.$t("modern_trade.sale_order.status_delivered");
        case 6: return this.$t("modern_trade.sale_order.status_closed");
        case 9: return this.$t("modern_trade.sale_order.status_cancelled");
        default: return "-";
      }
    },
    statusClass(s) {
      switch (s) {
        case 1: return "badge-secondary";
        case 2: return "badge-info";
        case 3: return "badge-warning";
        case 4: return "badge-warning";
        case 5: return "badge-primary";
        case 6: return "badge-success";
        case 9: return "badge-danger";
        default: return "badge-secondary";
      }
    },
    moStatusLabel(s) {
      switch (s) {
        case 1: return this.$t("modern_trade.sale_order.mo_status_issued") || "ออกแล้ว";
        case 2: return this.$t("modern_trade.sale_order.mo_status_received") || "รับแล้ว";
        case 9: return this.$t("modern_trade.sale_order.status_cancelled");
        default: return "-";
      }
    },
    invoiceStatusLabel(s) {
      switch (s) {
        case 1: return this.$t("modern_trade.invoice.status_unpaid");
        case 2: return this.$t("modern_trade.invoice.status_partial");
        case 3: return this.$t("modern_trade.invoice.status_paid");
        case 9: return this.$t("modern_trade.invoice.status_cancelled");
        default: return "-";
      }
    },
    invoiceStatusClass(s) {
      switch (s) {
        case 1: return "badge-warning";
        case 2: return "badge-info";
        case 3: return "badge-success";
        case 9: return "badge-danger";
        default: return "badge-secondary";
      }
    },
    money(v) {
      const n = Number(v) || 0;
      return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(v) {
      if (!v) return "-";
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      const pad = n => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },
  },
};
</script>

<style scoped>
/* #240 — แถวที่ LOT/วันหมดอายุไม่ครบ ต้องเห็นได้จากตัวแถวเอง ไม่ใช่แค่ป้ายในช่อง
   el-table วาด <tr> จาก component ลูก ⇒ scoped attribute ไม่ติดที่แถว ต้องใช้ >>>
   (dialog ตัวนี้ append-to-body แต่ตัวตารางยังเป็น element ของ template นี้อยู่) */
.mt-missing-lots >>> .mt-missing-lot-row td {
  background-color: #fdf1f0;
}
.mt-missing-lots >>> .mt-missing-expire-row td {
  background-color: #fdf6ec;
}
.mt-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}
/* แท็บ + ตารางต้องอ่านเป็น "ก้อนเดียวกัน": หัวแท็บทำหน้าที่เป็นส่วนหัวของการ์ดใบแรก
   (โค้งเฉพาะมุมบน, ไม่มีระยะห่างด้านล่าง) แล้วการ์ดใบแรกของแท็บตัดมุมโค้งด้านบนทิ้ง
   ลำดับการวาด: bootstrap ตั้ง .card เป็น position:relative และการ์ดอยู่หลัง .el-tabs__header
   ใน DOM ⇒ พื้นขาวของการ์ดถูกวาดทับเงาที่หัวแท็บสาดลงมา จึงไม่เกิดเงาซ้อนสองชั้นตรงรอยต่อ
   และกรอบเหลือง .mt-focus-highlight ของการ์ดใบแรกก็ยังวาดทับหัวแท็บได้ ไม่โดนบัง */
.mt-so-tabs >>> .el-tabs__header {
  background: #fff;
  border-radius: 0.375rem 0.375rem 0 0;
  padding: 0.5rem 0.75rem 0;
  margin-bottom: 0;
  box-shadow: 0 0 2rem 0 rgba(136, 152, 170, 0.15);
}
.mt-so-tabs >>> .el-tabs__content {
  overflow: visible;
}
/* v-if ที่เป็นเท็จเหลือไว้แค่ comment node ซึ่ง CSS ไม่นับเป็น element ⇒ :first-child
   เลื่อนไปเกาะการ์ด "ใบแรกที่แสดงจริง" ของแท็บนั้นเอง (สำคัญกับแท็บคลังสินค้าที่การ์ด
   ใบสั่งผลิต/นำเข้าสินค้าอาจไม่แสดง) */
.mt-so-tabs >>> .el-tab-pane > .card:first-child {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}
.mt-so-tabs >>> .el-tab-pane > .card:first-child > .card-header {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}
/* จอแคบ: element-ui ทำ nav ให้เลื่อนได้เองอยู่แล้ว แค่ลดขอบซ้ายขวาของหัวแท็บ
   เพื่อกันแท็บสุดท้ายถูกบีบจนล้น */
@media (max-width: 767.98px) {
  .mt-so-tabs >>> .el-tabs__header {
    padding: 0.5rem 0.25rem 0;
  }
}
/* round-19.1 deep-link: กรอบเหลืองไฮไลต์การ์ดที่เด้งมาจากช่องค้นเอกสาร ~2 วิ */
.mt-lot-product {
  border-top: 1px solid #e9ecef;
  padding: 0.75rem 0;
}
.mt-focus-highlight {
  box-shadow: 0 0 0 3px #ffc107, 0 0 2rem 0 rgba(136, 152, 170, 0.25) !important;
  transition: box-shadow 0.3s ease;
}
</style>
